// HelpExamples.jsx — literature reference-example gallery.
// Song et al. (1998): MeOAc and DME Batch reference examples.
// Al-Mayman et al. (2018): draft PFR example using Madhura-supplied Table 1 data.
// The previous synthetic Acetic Acid PFR showcase was retired per the
// 8-Oct-2026 client review. General user-defined Acetic Acid/PFR fitting remains
// available from Data Fitting; no synthetic AA PFR sample rows are shipped.
//
// IMPORTANT: after a completed run, graphs must come from that exact backend
// response. Do not substitute a static source-data chart and label it as a fit.

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import '../styles/HelpExamples.css';
// API endpoint resolution without a separate apiConfig module.
const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN ||
  ((typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:5000'
    : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000'))).replace(/\/$/, '');
const API = API_ORIGIN;

// Al-Mayman et al. (2018), Experimental section: 150 mm tube length and
// 6.35 mm inner diameter; 0.3 g catalyst in every experiment. The paper does
// not report bed void fraction or the reference state for ccm/min, so the
// example uses the full geometric tube volume and a 25 °C / 1 atm flow basis.
const MAYMAN_CATALYST_G = 0.3;
const MAYMAN_TUBE_LENGTH_M = 0.150;
const MAYMAN_TUBE_INNER_DIAMETER_M = 0.00635;
const MAYMAN_REACTOR_VOLUME_L = Math.PI * (MAYMAN_TUBE_INNER_DIAMETER_M / 2) ** 2
  * MAYMAN_TUBE_LENGTH_M * 1000;

const MAYMAN_PFR_CSV_HEADERS = [
  'Pressure', 'T', 'tau',
  'ethane_c2h6In', 'oxygen_o2In', 'ethene_c2h4In',
  'ethanoic_acid_c2h4o2In', 'COxIn', 'water_h2oIn',
  'ethane_c2h6Out', 'oxygen_o2Out', 'ethene_c2h4Out',
  'ethanoic_acid_c2h4o2Out', 'COxOut', 'water_h2oOut',
];

// Stage 1 is intentionally pressure-specific: these are the three groups
// Madhura identified in the 28-Oct review (workbook points are 1-based).
// Pressure in the PFR CSV is absolute psi; the paper's labels are gauge psi.
const MAYMAN_PRESSURE_CASES = [
  { key: 'p200psig', label: '200 psig', pointStart: 1, pointEnd: 18, startIndex: 0, endIndex: 18, pressureAtm: 14.609, pressurePsiAbs: 214.693, pressurePsig: 200 },
  { key: 'p100psig', label: '100 psig', pointStart: 19, pointEnd: 36, startIndex: 18, endIndex: 36, pressureAtm: 7.805, pressurePsiAbs: 114.702, pressurePsig: 100 },
  { key: 'p0psig',   label: '0 psig',   pointStart: 37, pointEnd: 63, startIndex: 36, endIndex: 63, pressureAtm: 1.000, pressurePsiAbs: 14.696, pressurePsig: 0 },
].map(group => ({ ...group, pointRange: `${group.pointStart}–${group.pointEnd}`, pointCount: group.pointEnd - group.pointStart + 1 }));

const cx = (...c) => c.filter(Boolean).join(' ');

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEMS
// ═══════════════════════════════════════════════════════════════════════════

// [MK 09-Sep] "Write full name, not symbols, here" — headings read
// "Methyl Acetate system" etc., with the reaction named in text underneath.
const SYSTEM_META = {
  meoac: {
    key: 'meoac', displayName: 'Methyl Acetate system', shortName: 'MeOAc', color: '#8b55db',
    subtitle: 'Esterification / hydrolysis reaction',
    equation: 'HOAc + MeOH ⇌ MeOAc + H₂O',
    reference: 'Song, Venimadhavan, Manning, Malone & Doherty, Ind. Eng. Chem. Res. 1998, 37, 1917–1928',
    doi: '10.1021/ie9708790',
    showReference: true,
  },
  dme: {
    key: 'dme', displayName: 'Dimethyl Ether system', shortName: 'DME', color: '#0ab5e2',
    subtitle: 'Methanol dehydration reaction',
    equation: '2 MeOH ⇌ DME + H₂O',
    reference: 'Song, Venimadhavan, Manning, Malone & Doherty, Ind. Eng. Chem. Res. 1998, 37, 1917–1928',
    doi: '10.1021/ie9708790',
    showReference: true,
  },
  mayman_2018: {
    key: 'mayman_2018', displayName: 'Mayman 2018 — Ethane Oxidation', shortName: 'Mayman 2018', color: '#2E7D32',
    subtitle: 'Table 1 · three pressure-specific groups · packed-bed PFR',
    equation: 'C₂H₆ + O₂ → C₂H₄ / CH₃COOH / COx (CO + CO₂ lump)',
    reference: 'Al-Mayman, Soliman, Al-Awadi & Al-Zeghayer, Applied Petrochemical Research 8, 29–38 (2018)',
    doi: '10.1007/s13203-018-0195-8',
    showReference: true,
    reviewStatus: 'DRAFT — pressure-specific exploratory fit; not a reproduction of the paper’s full mechanism',
  },
};

const REACTOR_GROUPS = [
  { key: 'batch', label: 'Batch Examples', systems: ['meoac', 'dme'] },
  { key: 'pfr',   label: 'PFR Examples',   systems: ['mayman_2018'] },
];

// ── Model-key vocabulary ────────────────────────────────────────────────────
// [MK 09-Sep] Three renames:
//   · "Change Single Run to Single Data Set"  → single_* labels + tag
//   · "Remove Global - Global"                → global_* keys are now multi_*
//   · "There should be 8 cases for each example" → multi_irrev_pl and
//     multi_irrev_lhhw added, giving 8 per system.
//
// KEY COLLISION NOTE: multi_irrev_* previously meant "multi-REACTION parallel
// system" for Acetic Acid. In the new vocabulary multi_* strictly means
// multi-DATASET. AA's three parallel reactions are a property of the
// chemistry, so they now show in the rate equation and description of every
// AA case instead of occupying two of the eight slots.
const MODEL_KEY_META = {
  single_rev_pl:     { label: 'Single Data Set – Reversible Power Law Model',     icon: '⇌', tag: 'Single Data Set' },
  single_rev_lhhw:   { label: 'Single Data Set – Reversible LHHW Model',          icon: '⇌', tag: 'Single Data Set' },
  multi_rev_pl:      { label: 'Multi-Dataset Fit – Reversible Power Law Model',   icon: '⇌', tag: 'Multiple Data Sets' },
  multi_rev_lhhw:    { label: 'Multi-Dataset Fit – Reversible LHHW Model',        icon: '⇌', tag: 'Multiple Data Sets' },
  single_irrev_pl:   { label: 'Single Data Set – Irreversible Power Law Model',   icon: '→', tag: 'Single Data Set' },
  single_irrev_lhhw: { label: 'Single Data Set – Irreversible LHHW Model',        icon: '→', tag: 'Single Data Set' },
  multi_irrev_pl:    { label: 'Multi-Dataset Fit – Irreversible Power Law Model', icon: '→', tag: 'Multiple Data Sets' },
  multi_irrev_lhhw:  { label: 'Multi-Dataset Fit – Irreversible LHHW Model',      icon: '→', tag: 'Multiple Data Sets' },
};

const MODEL_KEY_ORDER = [
  'single_rev_pl', 'single_rev_lhhw', 'multi_rev_pl', 'multi_rev_lhhw',
  'single_irrev_pl', 'single_irrev_lhhw', 'multi_irrev_pl', 'multi_irrev_lhhw',
];

// Rate equations. MeOAc eq 6 and DME eq 23 are the paper's own LHHW forms.
const MODEL_EQUATIONS = {
  meoac: {
    single_rev_pl:     'r = kf · xHOAc^a · xMeOH^b · (1 − Q/Keq)',
    single_rev_lhhw:   'r = k′S,1 · (aHOAc·aMeOH − aMeOAc·aH₂O/Keq₁) / (1 + ΣKᵢaᵢ)²   [Song eq 6]',
    multi_rev_pl:      'r = Af·exp(−Ea/RT) · xHOAc^a · xMeOH^b · (1 − Q/Keq)   shared Af/Ea',
    multi_rev_lhhw:    'r = k′S,1 · (aHOAc·aMeOH − aMeOAc·aH₂O/Keq₁) / (1 + ΣKᵢaᵢ)²   shared Af/Ea',
    single_irrev_pl:   'r = kf · xHOAc^a · xMeOH^b',
    single_irrev_lhhw: 'r = k′S,1 · aHOAc·aMeOH / (1 + ΣKᵢaᵢ)²',
    multi_irrev_pl:    'r = Af·exp(−Ea/RT) · xHOAc^a · xMeOH^b   shared Af/Ea',
    multi_irrev_lhhw:  'r = k′S,1 · aHOAc·aMeOH / (1 + ΣKᵢaᵢ)²   shared Af/Ea',
  },
  dme: {
    single_rev_pl:     'r = kf · xMeOH^a · (1 − Q/Keq)',
    single_rev_lhhw:   'r = k′S,2 · (a²MeOH − aDME·aH₂O/Keq₂) / (1 + KMeOH·aMeOH + KH₂O·aH₂O)²   [Song eq 23]',
    multi_rev_pl:      'r = Af·exp(−Ea/RT) · xMeOH^a · (1 − Q/Keq)   shared Af/Ea',
    multi_rev_lhhw:    'r = k′S,2 · (a²MeOH − aDME·aH₂O/Keq₂) / (1 + KMeOH·aMeOH + KH₂O·aH₂O)²   shared Af/Ea',
    single_irrev_pl:   'r = kf · xMeOH^a',
    single_irrev_lhhw: 'r = k′S,2 · a²MeOH / (1 + KMeOH·aMeOH + KH₂O·aH₂O)²',
    multi_irrev_pl:    'r = Af·exp(−Ea/RT) · xMeOH^a   shared Af/Ea',
    multi_irrev_lhhw:  'r = k′S,2 · a²MeOH / (1 + KMeOH·aMeOH + KH₂O·aH₂O)²   shared Af/Ea',
  },
  mayman_2018: {
    single_rev_pl:     'rᵢ = kfᵢ·∏xᵣᵉᵃᶜᵗᵃⁿᵗ^a − kbᵢ·∏xₚᵣₒdᵤcₜ^b  [generic platform fit]',
    single_rev_lhhw:   'rᵢ = [kfᵢ·∏xᵣᵉᵃᶜᵗᵃⁿᵗ^a − kbᵢ·∏xₚᵣₒdᵤcₜ^b] / (1 + ΣKⱼxⱼ)²  [generic platform fit]',
    single_irrev_pl:   'rᵢ = kfᵢ·∏xᵣᵉᵃᶜᵗᵃⁿᵗ^a  [generic platform fit]',
    single_irrev_lhhw: 'rᵢ = kfᵢ·∏xᵣᵉᵃᶜᵗᵃⁿᵗ^a / (1 + ΣKⱼxⱼ)²  [generic platform fit]',
  },
};

// [MK 09-Sep] Wording taken from the feedback document: a one-line headline
// plus the reactor/phase/basis detail underneath.
const DESCRIPTIONS = {
  single_rev_pl:     'Power Law model for temperature-dependent rate constants regressed using a single data set.',
  single_rev_lhhw:   'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using a single data set.',
  multi_rev_pl:      'Power Law model for temperature-dependent rate constants regressed using multiple data sets.',
  multi_rev_lhhw:    'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using multiple data sets.',
  single_irrev_pl:   'Forward-only Power Law model for temperature-dependent rate constants regressed using a single data set.',
  single_irrev_lhhw: 'Forward-only surface-mechanism LHHW model for temperature-dependent rate constants regressed using a single data set.',
  multi_irrev_pl:    'Forward-only Power Law model for temperature-dependent rate constants regressed using multiple data sets.',
  multi_irrev_lhhw:  'Forward-only surface-mechanism LHHW model for temperature-dependent rate constants regressed using multiple data sets.',
};

const DETAILS = {
  meoac: {
    pl:   'Batch reactor, liquid phase, mole-fraction basis. Regresses kf, exponents, and Keq.',
    lhhw: 'Langmuir-Hinshelwood-Hougen-Watson with adsorption terms. Adsorption constants from Song 1998, Table 5.',
  },
  dme: {
    pl:   'Batch reactor, vapor phase, mole-fraction basis. Regresses Af, Ea, and the MeOH reaction order.',
    lhhw: 'Dual-site Langmuir-Hinshelwood for DME synthesis. Adsorption constants from Song 1998, Table 5.',
  },
  mayman_2018: {
    pl:   'Packed-bed PFR, vapor phase, mole-fraction basis. Uses only the selected fixed-pressure subset from Madhura’s 63-point Mayman workbook. CO and CO₂ are lumped as COx for the initial platform-level three-reaction approximation.',
    lhhw: 'Packed-bed PFR, vapor phase, mole-fraction basis. Uses only the selected fixed-pressure subset from Madhura’s 63-point Mayman workbook. Generic species-level adsorption constants are used by the platform; they are not the paper’s reaction/site-specific constants or full 10-rate mechanism.',
  },
};

// ── Per-system reaction-condition defaults ──────────────────────────────────
// MeOAc values are Song 1998 Table 1, Run 1: 24 g catalyst,
// W = 0.00986 mol H+/mol mixture, 1.87 mol HOAc + 3.74 mol MeOH = 5.61 mol.
// DME values are Table 6, Run 5: 72 g catalyst, 9.88 mol MeOH.
const SYSTEM_DEFAULTS = {
  meoac: {
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    nMixMol: '5.61', wCatG: '24.0', vRxL: '0.306',
    singleTempC: '40', reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  dme: {
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    nMixMol: '10.47', wCatG: '72.0', vRxL: '0.400',
    singleTempC: '100', reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  mayman_2018: {
    // Simplified platform network: the measured CO and CO₂ are combined as
    // the pseudo-species COx. The third reaction uses Madhura's stated CO-pathway
    // coefficient (2.5 O₂); this fixed coefficient cannot exactly balance CO₂.
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'C2H4 + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'CH3COOH + H2O',
    rxn3Reactants: 'C2H6 + 2.5 O2', rxn3Products: '2 COx + 3 H2O',
    reactorMode: 'pfr', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    nMixMol: '', wCatG: String(MAYMAN_CATALYST_G), vRxL: String(MAYMAN_REACTOR_VOLUME_L),
    singleTempC: '', reactionPressure: '214.693', pressureUnit: 'psi', rateBasis: 'cat_weight',
  },
};

const MODEL_KEY_ORDER_BY_SYSTEM = {
  mayman_2018: ['single_rev_pl', 'single_rev_lhhw', 'single_irrev_pl', 'single_irrev_lhhw'],
};

// [MK 09-Sep] Generated so built-in systems keep the eight cases requested
// for the reference library. The Mayman example intentionally has
// only the four single-dataset cases requested for today's PFR showcase.
// drift apart. Also removes the six N/A Acetic Acid entries ("Why are some
// cases blanked out?") — perform_aa_fitting supports all four modes via
// normalize_aa_mode_v2 / is_reversible_mode, so there was no engine reason.
const makeExample = (system, d, modelKey, pressureCase = null) => {
  const isMulti = modelKey.startsWith('multi_');
  const isLhhw = modelKey.endsWith('_lhhw');
  const isRev = modelKey.includes('_rev_');
  const modelLabel = MODEL_KEY_META[modelKey].label;
  const pressureDetail = pressureCase
    ? ` This independent fit uses workbook points ${pressureCase.pointRange} (${pressureCase.pointCount} points) at ${pressureCase.label} (${pressureCase.pressurePsiAbs.toFixed(3)} psia absolute).`
    : '';
  return {
    id: pressureCase ? `${system}_${pressureCase.key}_${modelKey}` : `${system}_${modelKey}`,
    system,
    modelKey,
    pressureGroupKey: pressureCase?.key || null,
    pressureLabel: pressureCase ? `${pressureCase.label} (${pressureCase.pressurePsiAbs.toFixed(3)} psia abs)` : null,
    pointRange: pressureCase?.pointRange || null,
    label: pressureCase ? `${pressureCase.label} — ${modelLabel}` : modelLabel,
    description: DESCRIPTIONS[modelKey],
    detail: `${DETAILS[system][isLhhw ? 'lhhw' : 'pl']}${pressureDetail}`,
    rxn1Reactants: d.rxn1Reactants, rxn1Products: d.rxn1Products,
    rxn2Reactants: d.rxn2Reactants, rxn2Products: d.rxn2Products,
    rxn3Reactants: d.rxn3Reactants, rxn3Products: d.rxn3Products,
    inerts: d.inerts,
    reactorMode: d.reactorMode,
    reactionPhase: d.reactionPhase,
    concentrationBasis: d.concentrationBasis,
    reversibility: isRev ? 'reversible' : 'irreversible',
    modelType: isLhhw ? 'lhhw' : 'powerlaw',
    exponentMode: (isRev || isLhhw) ? 'fixed_1' : 'regress',
    keqMode: 'regress',
    fitScope: isMulti ? 'multiple' : 'single',
    nMixMol: d.nMixMol,
    wCatG: d.wCatG,
    vRxL: d.vRxL,
    reactionTempC: isMulti ? '' : d.singleTempC,
    reactionPressure: pressureCase ? pressureCase.pressurePsiAbs.toFixed(3) : d.reactionPressure,
    pressureUnit: d.pressureUnit,
    rateBasis: d.rateBasis,
    pressureCase: pressureCase || null,
    reviewStatus: d.reviewStatus || SYSTEM_META[system]?.reviewStatus || null,
  };
};

const EXAMPLES = ['meoac', 'dme', 'mayman_2018'].flatMap(system => {
  const d = SYSTEM_DEFAULTS[system];
  const modelOrder = MODEL_KEY_ORDER_BY_SYSTEM[system] || MODEL_KEY_ORDER;
  if (system === 'mayman_2018') {
    // Stage 1: each pressure group is its own single-dataset fit. The global
    // three-dataset fit is deliberately not exposed until these six reversible
    // PL/LHHW results (and the irreversible controls) have been inspected.
    return MAYMAN_PRESSURE_CASES.flatMap(pressureCase => {
      const pressureDefaults = {
        ...d,
        reactionPressure: pressureCase.pressurePsiAbs.toFixed(3),
        pressureUnit: 'psi',
      };
      return modelOrder.map(modelKey => makeExample(system, pressureDefaults, modelKey, pressureCase));
    });
  }
  return modelOrder.map(modelKey => makeExample(system, d, modelKey));
});

// ═══════════════════════════════════════════════════════════════════════════
// SONG 1998 MODEL — implemented directly in this file (no separate script).
//
//   Song, Venimadhavan, Manning, Malone & Doherty,
//   Ind. Eng. Chem. Res. 1998, 37, 1917-1928.  DOI 10.1021/ie9708790
//
// Wilson activity coefficients (Table 3) feeding the paper's own LHHW rate
// expressions (eq 6 for methyl acetate, eq 23 for DME), its adsorption
// constants (Table 5), Keq1 (eq 19), Keq2 (eq 24), and the two Arrhenius fits
// k'S,1 (eq 21) / k'S,2 (eq 25). Used below to GENERATE the multi-dataset
// example rows at the paper's own stated conditions (Table 1 for MeOAc,
// Table 6 for DME) for runs whose full time series live only in the paper's
// Supporting Information, not in the printed tables.
//
// VALIDATION — reproduces the paper's own Table 2 (MeOAc Run 1, all 20
// points, 4 species) at R² = 0.99, with zero fitted parameters:
//   const t = TABLE2.map(r => r.t);
//   const pred = simMeoac(TABLE2[0].x, 40, 0.00986, t);
//   // compare pred against TABLE2 -> R² ≈ 0.99, RMSE ≈ 0.007 mole fraction
// (TABLE2 is the same 20-row table transcribed as EXAMPLE_DATASETS.meoac
// .single.rows below — nothing here needs a second copy of it.)
// ═══════════════════════════════════════════════════════════════════════════

const R_CAL = 1.987; // cal/mol/K

// Table 3 — molar volumes [cm3/mol], order: HOAc, MeOH, MeOAc, H2O, DME.
const SONG_V = [57.54, 44.44, 79.84, 18.07, 69.07];

// Table 3 — Wilson interaction parameters A_ij [cal/mol].
const SONG_A = [
  [   0.0,    2535.2019, 1123.1444,  237.5248,  -96.6698],
  [-547.5248,    0.0,     813.1843,  107.3832,   900.9358],
  [-696.5031,  -31.1932,    0.0,     645.7225,   -17.2412],
  [ 658.0266,  469.5509, 1918.232,     0.0,      703.3566],
  [  96.7797, -418.6490,  -21.2317,  522.2653,     0.0   ],
];

// ln(gamma_i) = 1 - ln(sum_j x_j L_ij) - sum_k [ x_k L_ki / sum_j x_k L_kj ]
function songWilsonGamma(x, T_K) {
  const n = 5;
  const L = Array.from({ length: n }, () => new Array(n));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      L[i][j] = (SONG_V[j] / SONG_V[i]) * Math.exp(-SONG_A[i][j] / (R_CAL * T_K));
    }
  }
  const Sx = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let j = 0; j < n; j++) s += L[i][j] * x[j];
    Sx[i] = Math.max(s, 1e-30);
  }
  const g = new Array(n);
  for (let i = 0; i < n; i++) {
    let sum_k = 0;
    for (let k = 0; k < n; k++) sum_k += (x[k] * L[k][i]) / Sx[k];
    const lnG = 1 - Math.log(Sx[i]) - sum_k;
    g[i] = Math.exp(Math.max(-50, Math.min(50, lnG)));
  }
  return g;
}

// Table 5 adsorption constants.
const SONG_K_HOAc = 3.18, SONG_K_MeOH = 4.95, SONG_K_MeOAc = 0.82, SONG_K_H2O = 10.50;

const songKS1  = T_K => Math.exp(24.64 - 6287.7 / T_K);       // eq 21
const songKS2  = T_K => Math.exp(27.40 - 10654.0 / T_K);      // eq 25
const songKeq1 = T_K => 2.32 * Math.exp(782.98 / T_K);        // eq 19 / Table 5
const songKeq2 = T_K => {                                      // eq 24 (Nisoli 1997)
  const dG = -2.4634 - 1.5167e-3 * T_K; // kcal/mol
  return Math.exp(-dG / (0.001987 * T_K));
};

// eq 6 — x4 = [HOAc, MeOH, MeOAc, H2O] mole fractions; W = mol H+/mol mixture.
function songRateMeoac(x4, T_K, W) {
  const x5 = [x4[0], x4[1], x4[2], x4[3], 0.0];
  const g = songWilsonGamma(x5, T_K);
  const aHOAc = g[0] * x4[0], aMeOH = g[1] * x4[1], aMeOAc = g[2] * x4[2], aH2O = g[3] * x4[3];
  const num = aHOAc * aMeOH - (aMeOAc * aH2O) / songKeq1(T_K);
  const den = Math.pow(1 + SONG_K_HOAc * aHOAc + SONG_K_MeOH * aMeOH
    + SONG_K_MeOAc * aMeOAc + SONG_K_H2O * aH2O, 2);
  return songKS1(T_K) * W * num / den;
}

// eq 23 — x3 = [MeOH, H2O, DME] mole fractions; W = mol H+/mol mixture.
function songRateDme(x3, T_K, W) {
  const x5 = [0.0, x3[0], 0.0, x3[1], x3[2]];
  const g = songWilsonGamma(x5, T_K);
  const aMeOH = g[1] * x3[0], aH2O = g[3] * x3[1], aDME = g[4] * x3[2];
  const num = aMeOH * aMeOH - (aDME * aH2O) / songKeq2(T_K);
  const den = Math.pow(1 + SONG_K_MeOH * aMeOH + SONG_K_H2O * aH2O, 2);
  return songKS2(T_K) * W * num / den;
}

// Fixed-step RK4 — no ODE library available in-browser. 0.25-minute steps are
// well inside the stability region for these rate constants (verified: R²
// against Table 2 changes by < 0.001 for step sizes from 0.05 to 0.25 min).
function songRK4(rateFn, stoich, x0, T_K, W, tEval) {
  const out = [x0.slice()];
  let x = x0.slice(), t = tEval[0];
  const maxStep = 0.25;
  const deriv = (xx) => { const r = rateFn(xx, T_K, W); return stoich.map(s => s * r); };
  for (let k = 1; k < tEval.length; k++) {
    const tTarget = tEval[k];
    while (t < tTarget - 1e-9) {
      const h = Math.min(maxStep, tTarget - t);
      const k1 = deriv(x);
      const x2 = x.map((v, i) => v + (h / 2) * k1[i]);
      const k2 = deriv(x2);
      const x3 = x.map((v, i) => v + (h / 2) * k2[i]);
      const k3 = deriv(x3);
      const x4 = x.map((v, i) => v + h * k3[i]);
      const k4 = deriv(x4);
      x = x.map((v, i) => v + (h / 6) * (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i]));
      x = x.map(v => Math.max(0, Math.min(1, v))); // mole fractions only
      t += h;
    }
    out.push(x.slice());
  }
  return out;
}

// stoich: d[HOAc]/dt = -r, d[MeOH]/dt = -r, d[MeOAc]/dt = +r, d[H2O]/dt = +r
const songSimMeoac = (x0, T_C, W, tEval) =>
  songRK4(songRateMeoac, [-1, -1, 1, 1], x0, T_C + 273.15, W, tEval);

// stoich: d[MeOH]/dt = -2r, d[H2O]/dt = +r, d[DME]/dt = +r
const songSimDme = (x0, T_C, W, tEval) =>
  songRK4(songRateDme, [-2, 1, 1], x0, T_C + 273.15, W, tEval);

// Builds a displayCols-shaped row array from a simulated state, formatted to
// 4 decimal places to match how the published rows in this file are written.
const songRowsMeoac = (x0, T_C, W, times) =>
  songSimMeoac(x0, T_C, W, times).slice(1).map((x, i) => ([
    `${times[i + 1]}`,
    x[2].toFixed(4), // MeOAc
    x[1].toFixed(4), // MeOH
    x[3].toFixed(4), // H2O
    x[0].toFixed(4), // HOAc
  ]));

const songRowsDme = (x0, T_C, W, times) =>
  songSimDme(x0, T_C, W, times).slice(1).map((x, i) => ([
    `${times[i + 1]}`,
    x[0].toFixed(4), // MeOH
    x[1].toFixed(4), // H2O
    x[2].toFixed(4), // DME
  ]));


// [MK 09-Sep] "If it is an example, the data set should load automatically.
// A single data set and 2 data sets with 5-6 points each should load
// automatically; otherwise, it is not a true example."
//
// Every system therefore carries BOTH .single and .multi, and nothing on this
// page asks the user to upload a file.
//
// ── PROVENANCE ──────────────────────────────────────────────────────────────
// `source: 'published'` rows are transcribed from a printed table.
// `source: 'derived'`  rows are computed from that paper's own validated
//                      model at conditions the paper states, for runs whose
//                      time series live in its Supporting Information.
// The UI shows this distinction on every dataset — a reader should never have
// to guess which numbers were measured.
//
//   Song, Venimadhavan, Manning, Malone & Doherty,
//   Ind. Eng. Chem. Res. 1998, 37, 1917-1928.  DOI 10.1021/ie9708790
//
// The derived MeOAc/DME rows are computed IN THIS FILE by implementing the
// paper's Wilson activity model (Table 3) with its LHHW rate expressions
// (eq 6 / eq 23), adsorption constants (Table 5: K_H2O 10.50, K_MeOH 4.95,
// K_HOAc 3.18, K_MeOAc 0.82), Keq1 = 2.32·exp(782.98/T) and
// k'S,1 = exp(24.64 − 6287.7/T) (eq 21), Keq2 from eq 24 and
// k'S,2 = exp(27.40 − 10654.0/T) (eq 25). See the "SONG 1998 MODEL" block
// below — no external script or service is involved.
//
// VALIDATION: that implementation reproduces the paper's own Table 2 (MeOAc
// Run 1, all 20 points, 4 species) at R² ≈ 0.99, with zero fitted parameters.
// The multi-dataset rows below are generated by the same functions at the
// Table 1 / Table 6 conditions for the other runs.
//
// CAVEAT ON THE DME TABLE: Song's Table 7 is internally inconsistent about its
// own conditions — the table title says Run 10 (Table 6: 115 °C, 71.0 g), the
// body text and the Figure 7 caption both say 85 °C / 72 g (= Run 11).
// Simulating the data against the paper's model fits neither: 115 °C badly
// over-predicts (x_DME = 0.205 vs 0.110 measured at 720 min) and 85 °C badly
// under-predicts (0.037). A temperature scan puts the best fit near 105 °C.
// The measured numbers are used verbatim and labelled 100 °C, which is what
// Table 6 lists for 9 of its 11 runs and what this app already used. Treat the
// DME temperature as approximate until the original authors' raw run is
// available.
//
// ── REFERENCE DATASETS ─────────────────────────────────────────────────────
// MeOAc and DME are Batch examples from Song et al. (1998). Mayman 2018
// is the PFR example. Stage 1 splits its 63 source workbook rows into the
// three fixed-pressure groups Madhura identified; each card uses exactly one
// group. The global fit is deferred until the independent runs are reviewed.
// ═══════════════════════════════════════════════════════════════════════════

const MEOAC_COLS = ['Time (min)', 'x-MeOAc', 'x-MeOH', 'x-H2O', 'x-HOAc'];
const DME_COLS   = ['Time (min)', 'x-MeOH', 'x-H2O', 'x-DME'];

const EXAMPLE_DATASETS = {
  // ── Methyl Acetate — batch, liquid ──────────────────────────────────────
  meoac: {
    speciesForWeights: ['MeOAc', 'MeOH', 'H2O', 'HOAc'],
    chartX: 0, chartY: [1, 2, 3, 4], chartYLabel: 'Mole fraction',
    single: {
      label: 'Run 1 — 40 °C',
      source: 'published',
      provenance: 'Song 1998, Table 2 — Run 1, all 20 measured points. '
        + '40 °C, 24 g Amberlyst 15W (W = 0.00986 mol H⁺/mol mixture), '
        + '1.87 mol HOAc + 3.74 mol MeOH.',
      displayCols: MEOAC_COLS, csvHeader: MEOAC_COLS,
      meta: { T_C: 40, n_mix_mol: 5.61, W_cat_g: 24.0 },
      rows: [
        ['1.5',  '0.0053', '0.6005', '0.1063', '0.2879'],
        ['20',   '0.0551', '0.5402', '0.1550', '0.2497'],
        ['40',   '0.0917', '0.5074', '0.1884', '0.2125'],
        ['60',   '0.1163', '0.4746', '0.2258', '0.1833'],
        ['90',   '0.1477', '0.4559', '0.2371', '0.1593'],
        ['120',  '0.1682', '0.4356', '0.2638', '0.1323'],
        ['150',  '0.1826', '0.4189', '0.2809', '0.1176'],
        ['180',  '0.1958', '0.4096', '0.2889', '0.1057'],
        ['240',  '0.2143', '0.3893', '0.3117', '0.0848'],
        ['300',  '0.2276', '0.3794', '0.3213', '0.0716'],
        ['360',  '0.2391', '0.3749', '0.3230', '0.0631'],
        ['420',  '0.2453', '0.3680', '0.3316', '0.0551'],
        ['450',  '0.2462', '0.3647', '0.3323', '0.0568'],
        ['540',  '0.2513', '0.3583', '0.3405', '0.0499'],
        ['600',  '0.2525', '0.3534', '0.3480', '0.0461'],
        ['660',  '0.2523', '0.3499', '0.3512', '0.0466'],
        ['720',  '0.2560', '0.3513', '0.3477', '0.0450'],
        ['810',  '0.2578', '0.3489', '0.3516', '0.0418'],
        ['900',  '0.2563', '0.3484', '0.3531', '0.0421'],
        ['1390', '0.2572', '0.3441', '0.3582', '0.0404'],
      ],
    },
    // Table 1 Runs 1 / 5 / 7 are a matched series: identical charge
    // (1.87 HOAc + 3.74 MeOH) and identical catalyst (24 g, W = 0.00986),
    // with only temperature differing (40 / 45 / 50 °C). That is exactly the
    // structure a shared-Af/Ea fit wants, so those are the runs used here.
    //
    // Rows are computed by songRowsMeoac() at load time, not pasted numbers —
    // change SONG_A/SONG_V/the rate constants above and these regenerate.
    // x0 matches Table 2's first row (t = 1.5 min): [HOAc, MeOH, MeOAc, H2O].
    multi: (() => {
      const x0 = [0.2879, 0.6005, 0.0053, 0.1063];
      const times = [1.5, 15, 40, 90, 180, 360, 720];
      const runs = [
        { label: 'Run 1 — 40 °C', T_C: 40, runNo: 1 },
        { label: 'Run 5 — 45 °C', T_C: 45, runNo: 5 },
        { label: 'Run 7 — 50 °C', T_C: 50, runNo: 7 },
      ];
      return runs.map(({ label, T_C, runNo }) => ({
        label,
        source: 'derived',
        provenance: `Song 1998, Table 1 Run ${runNo} conditions (${T_C} °C, 24 g, `
          + 'W = 0.00986 — same charge as the other two runs in this set). Time '
          + 'series is in the paper’s Supporting Information; these six points '
          + 'are computed in this file from the paper’s own LHHW model (songSimMeoac), '
          + 'which reproduces the paper’s printed Table 2 at R² = 0.99.',
        displayCols: MEOAC_COLS, csvHeader: MEOAC_COLS,
        meta: { T_C, n_mix_mol: 5.61, W_cat_g: 24.0 },
        rows: songRowsMeoac(x0, T_C, 0.00986, times),
      }));
    })(),
  },

  // ── Dimethyl Ether — batch, vapor ───────────────────────────────────────
  dme: {
    speciesForWeights: ['MeOH', 'H2O', 'DME'],
    chartX: 0, chartY: [1, 2, 3], chartYLabel: 'Mole fraction',
    single: {
      label: 'Methanol dehydration run — ~100 °C',
      source: 'published',
      provenance: 'Song 1998, Table 7 — all 7 measured points. The paper is '
        + 'inconsistent about this run’s temperature (title says Run 10 / 115 °C, '
        + 'text and Figure 7 say 85 °C); the data itself best fits ≈105 °C. '
        + 'Labelled 100 °C here, matching 9 of the 11 runs in Table 6.',
      displayCols: DME_COLS, csvHeader: DME_COLS,
      meta: { T_C: 100, n_mix_mol: 10.47, W_cat_g: 72.0 },
      rows: [
        ['5',   '0.9361', '0.0627', '0.0012'],
        ['120', '0.8829', '0.0856', '0.0315'],
        ['240', '0.8213', '0.1230', '0.0557'],
        ['360', '0.7710', '0.1534', '0.0757'],
        ['480', '0.7230', '0.1739', '0.1031'],
        ['600', '0.7020', '0.1968', '0.1012'],
        ['720', '0.6625', '0.2272', '0.1103'],
      ],
    },
    // Table 6 spans 85 / 100 / 115 °C at the paper's own catalyst loadings.
    // Computed by songRowsDme() at load time from the same eq 23 model.
    // x0 matches Table 7's first row (t = 5 min): [MeOH, H2O, DME].
    multi: (() => {
      const x0 = [0.9361, 0.0627, 0.0012];
      const times = [5, 120, 240, 360, 480, 600, 720];
      const runs = [
        { label: 'Run 11 — 85 °C',  T_C: 85,  W: 0.02856, runNo: 11 },
        { label: 'Run 5 — 100 °C',  T_C: 100, W: 0.02829, runNo: 5 },
        { label: 'Run 10 — 115 °C', T_C: 115, W: 0.02861, runNo: 10 },
      ];
      return runs.map(({ label, T_C, W, runNo }) => ({
        label,
        source: 'derived',
        provenance: `Song 1998, Table 6 Run ${runNo} conditions (${T_C} °C, `
          + `W = ${W}). Computed in this file from the paper’s eq 23 LHHW model `
          + '(songSimDme).',
        displayCols: DME_COLS, csvHeader: DME_COLS,
        meta: { T_C, n_mix_mol: 10.47, W_cat_g: T_C === 115 ? 71.0 : 72.0 },
        rows: songRowsDme(x0, T_C, W, times),
      }));
    })(),
  },

  // ── Mayman 2018 — packed-bed PFR, Table 1 (63 operating conditions) ──
  // Source paper: Al-Mayman et al., Applied Petrochemical Research 8 (2018),
  // 29–38, DOI 10.1007/s13203-018-0195-8. Outlet mole fractions are from the
  // supplied ethane_oxidation_mole_fractions_atm.xlsx workbook; they are
  // transformed from Table 1 conversion/selectivity values, not values printed
  // as mole fractions in the paper. CO and CO₂ are summed into COx, matching
  // the lumped CO+(CO₂) node in the paper's Fig. 1.
  //
  // The workbook's "Residence Time (min)" column is the numeric W/F_C2H6
  // space-time (g-cat·min/mL), not physical minutes. Infer F_C2H6 = W/(W/F),
  // then F_total = F_C2H6/y_C2H6,in. The generic PFR engine uses seconds, so tau
  // is estimated from total feed flow, ideal-gas T/P scaling and tube geometry
  // (ID 6.35 mm × 150 mm). Assumptions: flow reference 25 °C / 1 atm and full
  // geometric tube volume (4.750 mL); no packed-bed void correction is available.
  // Since outlet compositions are calculated from reported conversion/selectivity,
  // the workbook's four-decimal outlet fractions can sum slightly above unity.
  // Only the balance-derived H2O outlet is adjusted downward to four decimals,
  // preserving all other source values and keeping the sum <= 1. COx uses the
  // client-specified CO-based 2.5 O2 coefficient; the lump is not fully balanced
  // for the CO2 fraction. This remains an exploratory platform example.
  mayman_2018: {
    speciesForWeights: ['C2H6', 'O2', 'C2H4', 'CH3COOH', 'COx', 'H2O'],
    chartX: 2, chartY: [9, 11, 12, 13],
    chartYLabel: 'Outlet mole fraction',
    chartXLabel: 'Effective PFR coordinate τ (s-equivalent)',
    single: {
      label: 'Mayman 2018 — Table 1 (63 conditions)',
      source: 'digitized',
      provenance: 'Al-Mayman et al. (2018), Table 1, 63 operating conditions, using supplied workbook. Source Table 1 reports conversion and selectivity; the workbook converts these to outlet mole fractions. CO and CO₂ are combined as the lumped pseudo-species COx to mirror Fig. 1. The workbook “Residence Time (min)” is the numeric W/F_C₂H₆ space-time (g-cat·min/mL), not physical minutes. It is converted to the generic PFR engine’s seconds-based coordinate by inferring F_C₂H₆ = W/(W/F_C₂H₆), converting to total feed flow F_total = F_C₂H₆/y_C₂H₆,in, then applying ideal-gas T/P scaling and the 4.750 mL tube volume. Assumptions: feed-flow reference 25 °C / 1 atm and full geometric tube volume; no packed-bed void correction. The balance-derived H₂O outlet is corrected at 4 decimals for row-sum rounding; the other source outlet fractions are preserved. The fitted 3-reaction Power-Law/LHHW network is an exploratory platform approximation, not the paper’s full mechanistic model. The COx lump uses the client-specified CO-based 2.5 O₂ coefficient, which cannot exactly represent the CO₂ fraction.',
      displayCols: [
        'P (psi abs)', 'T (°C)', 'τ equivalent (s)',
        'C₂H₆ in', 'O₂ in', 'C₂H₄ in', 'CH₃COOH in', 'COx in', 'H₂O in',
        'C₂H₆ out', 'O₂ out', 'C₂H₄ out', 'CH₃COOH out', 'COx out', 'H₂O out',
      ],
      csvHeader: ['Pressure', 'T', 'tau', 'ethane_c2h6In', 'oxygen_o2In', 'ethene_c2h4In', 'ethanoic_acid_c2h4o2In', 'COxIn', 'water_h2oIn', 'ethane_c2h6Out', 'oxygen_o2Out', 'ethene_c2h4Out', 'ethanoic_acid_c2h4o2Out', 'COxOut', 'water_h2oOut'],
      meta: {
        T_range_C: '225–275', P_range_psi: '14.7–214.7 absolute',
        flow_range_ccm_min: '10–40', W_cat_g: MAYMAN_CATALYST_G,
        V_rx_L: MAYMAN_REACTOR_VOLUME_L,
        tau_min_s: 3.8757,
        tau_max_s: 249.2155,
      },
      rows: [
        ["214.693116", "225.000000", "249.215477", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.9059", "0.0005", "0.0008", "0.0316", "0.0116", "0.0496"],
        ["214.693116", "225.000000", "249.215477", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8106", "0.0541", "0.0006", "0.0462", "0.0166", "0.0719"],
        ["214.693116", "240.000000", "241.930605", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7935", "0.0015", "0.0003", "0.0662", "0.0288", "0.1097"],
        ["214.693116", "225.000000", "249.215477", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5873", "0.2480", "0.0005", "0.0476", "0.0273", "0.0893"],
        ["214.693116", "225.000000", "124.607739", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8913", "0.0347", "0.0020", "0.0234", "0.0093", "0.0393"],
        ["214.693116", "240.000000", "120.965302", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8858", "0.0006", "0.0019", "0.0362", "0.0150", "0.0605"],
        ["214.693116", "225.000000", "124.607738", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7834", "0.1191", "0.0010", "0.0323", "0.0124", "0.0518"],
        ["214.693116", "240.000000", "120.965302", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7838", "0.0577", "0.0009", "0.0493", "0.0233", "0.0850"],
        ["214.693116", "250.000000", "118.653054", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7789", "0.0061", "0.0007", "0.0649", "0.0336", "0.1158"],
        ["214.693116", "225.000000", "124.607738", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5852", "0.3156", "0.0010", "0.0330", "0.0125", "0.0527"],
        ["214.693116", "250.000000", "118.653053", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5546", "0.1633", "0.0007", "0.0707", "0.0558", "0.1549"],
        ["214.693116", "275.000000", "113.241530", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5374", "0.0003", "0.0002", "0.0978", "0.1064", "0.2579"],
        ["214.693116", "225.000000", "62.303869", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8903", "0.0698", "0.0021", "0.0116", "0.0050", "0.0212"],
        ["214.693116", "250.000000", "59.326527", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8956", "0.0215", "0.0016", "0.0249", "0.0119", "0.0445"],
        ["214.693116", "225.000000", "62.303870", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7897", "0.1673", "0.0018", "0.0136", "0.0048", "0.0228"],
        ["214.693116", "250.000000", "59.326526", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7693", "0.1025", "0.0013", "0.0395", "0.0186", "0.0688"],
        ["214.693116", "225.000000", "62.303869", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5944", "0.3587", "0.0013", "0.0138", "0.0068", "0.0250"],
        ["214.693116", "250.000000", "59.326527", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5682", "0.2995", "0.0012", "0.0363", "0.0230", "0.0718"],
        ["114.701880", "225.000000", "133.145787", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.9087", "0.0254", "0.0006", "0.0223", "0.0080", "0.0350"],
        ["114.701880", "225.000000", "133.145786", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7901", "0.1168", "0.0007", "0.0307", "0.0121", "0.0496"],
        ["114.701880", "250.000000", "126.783090", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7606", "0.0021", "0.0007", "0.0781", "0.0318", "0.1267"],
        ["114.701880", "225.000000", "133.145786", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5777", "0.3228", "0.0006", "0.0293", "0.0158", "0.0538"],
        ["114.701880", "250.000000", "126.783090", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5589", "0.1700", "0.0007", "0.0727", "0.0497", "0.1480"],
        ["114.701880", "275.000000", "121.000773", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5303", "0.0002", "0.0004", "0.1092", "0.1002", "0.2597"],
        ["114.701880", "225.000000", "66.572893", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8764", "0.0778", "0.0023", "0.0144", "0.0050", "0.0241"],
        ["114.701880", "225.000000", "66.572893", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5849", "0.3511", "0.0114", "0.0152", "0.0080", "0.0294"],
        ["114.701880", "250.000000", "63.391545", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5703", "0.2837", "0.0012", "0.0370", "0.0278", "0.0800"],
        ["114.701880", "225.000000", "33.286447", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.9005", "0.0883", "0.0038", "0.0000", "0.0014", "0.0060"],
        ["114.701880", "250.000000", "31.695773", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.9102", "0.0586", "0.0030", "0.0050", "0.0061", "0.0171"],
        ["114.701880", "275.000000", "30.250193", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8924", "0.0037", "0.0062", "0.0215", "0.0194", "0.0568"],
        ["114.701880", "225.000000", "33.286446", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7847", "0.1849", "0.0036", "0.0080", "0.0029", "0.0159"],
        ["114.701880", "250.000000", "31.695773", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7818", "0.1511", "0.0034", "0.0125", "0.0195", "0.0317"],
        ["114.701880", "275.000000", "30.250193", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7624", "0.0489", "0.0023", "0.0480", "0.0352", "0.1032"],
        ["114.701880", "225.000000", "33.286447", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5995", "0.3810", "0.0023", "0.0033", "0.0033", "0.0106"],
        ["114.701880", "250.000000", "31.695773", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5786", "0.3453", "0.0020", "0.0212", "0.0119", "0.0410"],
        ["114.701880", "275.000000", "30.250193", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5394", "0.2329", "0.0013", "0.0500", "0.0554", "0.1210"],
        ["14.695949", "225.000000", "17.059037", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8969", "0.0853", "0.0089", "0.0000", "0.0000", "0.0089"],
        ["14.695949", "250.000000", "16.243830", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8970", "0.0592", "0.0156", "0.0000", "0.0050", "0.0232"],
        ["14.695949", "275.000000", "15.502982", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8840", "0.0102", "0.0191", "0.0000", "0.0271", "0.0596"],
        ["14.695949", "225.000000", "17.059038", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7988", "0.1849", "0.0082", "0.0000", "0.0000", "0.0081"],
        ["14.695949", "250.000000", "16.243830", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7981", "0.1591", "0.0132", "0.0000", "0.0065", "0.0231"],
        ["14.695949", "275.000000", "15.502982", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8030", "0.0867", "0.0154", "0.0000", "0.0318", "0.0631"],
        ["14.695949", "225.000000", "17.059037", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5938", "0.3886", "0.0071", "0.0000", "0.0013", "0.0092"],
        ["14.695949", "250.000000", "16.243829", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5973", "0.3604", "0.0114", "0.0000", "0.0078", "0.0231"],
        ["14.695949", "275.000000", "15.502982", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.6069", "0.2743", "0.0114", "0.0000", "0.0384", "0.0690"],
        ["14.695949", "225.000000", "8.529519", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8972", "0.0934", "0.0047", "0.0000", "0.0000", "0.0047"],
        ["14.695949", "250.000000", "8.121915", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8929", "0.0837", "0.0092", "0.0000", "0.0020", "0.0122"],
        ["14.695949", "275.000000", "7.751491", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8948", "0.0518", "0.0143", "0.0000", "0.0099", "0.0292"],
        ["14.695949", "225.000000", "8.529518", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7979", "0.1930", "0.0046", "0.0000", "0.0000", "0.0045"],
        ["14.695949", "250.000000", "8.121915", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7914", "0.1859", "0.0091", "0.0000", "0.0018", "0.0118"],
        ["14.695949", "275.000000", "7.751491", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7908", "0.1427", "0.0137", "0.0000", "0.0157", "0.0371"],
        ["14.695949", "225.000000", "8.529518", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5975", "0.3937", "0.0044", "0.0000", "0.0000", "0.0044"],
        ["14.695949", "250.000000", "8.121915", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5943", "0.3837", "0.0077", "0.0000", "0.0027", "0.0116"],
        ["14.695949", "275.000000", "7.751491", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5907", "0.3426", "0.0110", "0.0000", "0.0179", "0.0378"],
        ["14.695949", "225.000000", "4.264759", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8980", "0.0952", "0.0034", "0.0000", "0.0000", "0.0034"],
        ["14.695949", "250.000000", "4.060957", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8972", "0.0893", "0.0068", "0.0000", "0.0000", "0.0067"],
        ["14.695949", "275.000000", "3.875746", "0.900000", "0.100000", "0.000000", "0.000000", "0.000000", "0.000000", "0.8957", "0.0710", "0.0118", "0.0000", "0.0039", "0.0176"],
        ["14.695949", "225.000000", "4.264759", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7990", "0.1941", "0.0035", "0.0000", "0.0000", "0.0034"],
        ["14.695949", "250.000000", "4.060958", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7988", "0.1880", "0.0066", "0.0000", "0.0000", "0.0066"],
        ["14.695949", "275.000000", "3.875746", "0.800000", "0.200000", "0.000000", "0.000000", "0.000000", "0.000000", "0.7958", "0.1672", "0.0115", "0.0000", "0.0057", "0.0198"],
        ["14.695949", "225.000000", "4.264759", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.6022", "0.3919", "0.0030", "0.0000", "0.0000", "0.0029"],
        ["14.695949", "250.000000", "4.060957", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5964", "0.3881", "0.0061", "0.0000", "0.0014", "0.0080"],
        ["14.695949", "275.000000", "3.875746", "0.600000", "0.400000", "0.000000", "0.000000", "0.000000", "0.000000", "0.5918", "0.3704", "0.0093", "0.0000", "0.0077", "0.0208"],
      ],
    },
  },
};


// Madhura requested no more than two decimal places for temperature and four
// decimal places for compositions. Keep the canonical 15-column backend schema;
// pressure is absolute psi (3 decimals) and the effective tau coordinate is
// retained to four decimals for a readable, consistent reference table.
const formatMaymanRow = (row) => row.map((raw, index) => {
  const value = Number(raw);
  if (!Number.isFinite(value)) return raw;
  if (index === 0) return value.toFixed(3);       // Pressure, psi absolute
  if (index === 1) return String(Math.round(value)); // Temperature, °C (source values are integers)
  if (index === 2) return value.toFixed(4);       // effective tau, seconds-equivalent
  return value.toFixed(4);                        // all inlet/outlet mole fractions
});

const maymanBundle = EXAMPLE_DATASETS.mayman_2018;
const maymanRowsAll = maymanBundle.single.rows.map(formatMaymanRow);
maymanBundle.single = {
  ...maymanBundle.single,
  rows: maymanRowsAll,
  label: 'Mayman 2018 — all Table 1 points (reference only; not used as a Stage 1 fit)',
  meta: { ...maymanBundle.single.meta, P_range_psi: '14.696–214.693 psia' },
};
maymanBundle.pressureDatasets = MAYMAN_PRESSURE_CASES.map((pressureCase) => {
  const rows = maymanRowsAll.slice(pressureCase.startIndex, pressureCase.endIndex);
  const expectedPressure = pressureCase.pressurePsiAbs;
  const hasUnexpectedPressure = rows.some(row => Math.abs(Number(row[0]) - expectedPressure) > 0.001);
  if (rows.length !== pressureCase.pointCount || hasUnexpectedPressure) {
    throw new Error(`Mayman Stage 1 dataset ${pressureCase.key} failed point-count/pressure validation.`);
  }
  return {
    ...maymanBundle.single,
    key: pressureCase.key,
    label: `Mayman 2018 — ${pressureCase.label} · points ${pressureCase.pointRange}`,
    provenance: `Al-Mayman et al. (2018), Table 1, using Madhura’s supplied workbook. This Stage 1 single-dataset case contains source workbook points ${pressureCase.pointRange} (${pressureCase.pointCount} points) at ${pressureCase.label}; CSV pressure is ${expectedPressure.toFixed(3)} psia absolute. The workbook converts the paper’s conversion/selectivity data to outlet mole fractions. CO and CO₂ remain combined as COx for this initial platform example, as permitted in the 28-Oct review. The workbook “Residence Time (min)” is the numeric W/F_C₂H₆ space-time (g-cat·min/mL), not physical minutes. The generic PFR coordinate is converted to a seconds-equivalent value using the documented 25 °C / 1 atm flow-reference and full geometric tube-volume assumption; no packed-bed void correction is available. The balance-derived H₂O outlet is adjusted only to account for four-decimal source rounding. This is an exploratory platform model, not the paper’s full mechanistic model.`,
    rows,
    meta: {
      ...maymanBundle.single.meta,
      P_range_psi: null,
      P_psi: expectedPressure,
      P_psi_abs: expectedPressure,
      P_psig: pressureCase.pressurePsig,
      P_atm_workbook: pressureCase.pressureAtm,
      pressureLabel: `${pressureCase.label} (${expectedPressure.toFixed(3)} psia absolute)`,
      pointRange: pressureCase.pointRange,
      pointCount: pressureCase.pointCount,
      tau_min_s: Math.min(...rows.map(row => Number(row[2]))),
      tau_max_s: Math.max(...rows.map(row => Number(row[2]))),
    },
  };
});

// Always an array: one dataset for a single-dataset case, several for a global fit.
// For Mayman Stage 1, select exactly the pressure group tied to the card.
const datasetsFor = (ex) => {
  const bundle = EXAMPLE_DATASETS[ex.system];
  if (!bundle) return [];
  if (ex.system === 'mayman_2018') {
    const pressureDataset = bundle.pressureDatasets?.find(ds => ds.key === ex.pressureGroupKey);
    return pressureDataset ? [pressureDataset] : [];
  }
  return ex.fitScope === 'multiple' ? bundle.multi : [bundle.single];
};

const parseSpeciesNames = (str) => (str || '')
  .split('+')
  .map(s => s.trim().replace(/^[\d.]+\s*/, ''))
  .filter(Boolean);

// Builds the DWSIM-style "Initial Estimates" table. Display only — real fitted
// values come from a backend run and appear under "Latest Parameters".
//
// MeOAc/DME LHHW starting values are Song 1998 Table 5 rather than generic
// guesses, so an LHHW example starts where the paper's own regression landed.
const SONG_K = { 'HOAc': 3.18, 'MeOH': 4.95, 'MeOAc': 0.82, 'H₂O': 10.50, 'H2O': 10.50, 'DME': 0.82 };

const buildInitialEstimates = (ex) => {
  const rows = [];
  const isRev = ex.reversibility === 'reversible';
  const reactants1 = parseSpeciesNames(ex.rxn1Reactants);
  const products1  = parseSpeciesNames(ex.rxn1Products);
  const songSystem = ex.system === 'meoac' || ex.system === 'dme';

  const addRateConstants = (suffix = '') => {
    // Song eq 21 / eq 25 preexponentials and activation energies where they
    // apply; generic bounds otherwise.
    const isDme = ex.system === 'dme';
    const Af = songSystem ? (isDme ? 7.9e11 : 5.02e10) : 1.0;
    const Ea = songSystem ? (isDme ? 88.6 : 52.3) : 50.0;   // kJ/mol (21.17 / 12.49 kcal)
    rows.push({ name: `Af${suffix} (kmol·kg⁻¹cat·s⁻¹)`, lower: 0, value: Af, upper: 1e14 });
    rows.push({ name: `Ea_f${suffix} (kJ/mol)`,          lower: 0, value: Ea, upper: 200 });
    if (isRev) {
      rows.push({ name: `Ab${suffix} (kmol·kg⁻¹cat·s⁻¹)`, lower: 0, value: Af / 20, upper: 1e14 });
      rows.push({ name: `Ea_b${suffix} (kJ/mol)`,          lower: 0, value: Ea + 5, upper: 200 });
    }
  };

  // Mayman example uses the generic PFR engine. These are deliberately
  // generic starting guesses, not the paper's fitted mechanistic constants.
  if (ex.system === 'mayman_2018') {
    [1, 2, 3].forEach(i => addRateConstants(` (Rxn ${i})`));
    if (ex.modelType === 'lhhw') {
      ['C2H6', 'O2', 'C2H4', 'H2O', 'CH3COOH', 'COx'].forEach(sp =>
        rows.push({ name: `K(${sp})`, lower: 0, value: 1.0, upper: 50 }));
    }
    return rows;
  }

  addRateConstants();
  if (ex.exponentMode === 'regress') {
    reactants1.forEach(sp => rows.push({ name: `a(${sp}) fwd`, lower: 0, value: 1.0, upper: 3 }));
    if (isRev) products1.forEach(sp => rows.push({ name: `a(${sp}) rev`, lower: 0, value: 1.0, upper: 3 }));
  }
  if (ex.modelType === 'lhhw') {
    [...reactants1, ...products1].forEach(sp =>
      rows.push({ name: `K(${sp})`, lower: 0, value: SONG_K[sp] ?? 0.5, upper: 50 }));
  } else if (isRev) {
    // Song Table 4: Keq = 30.2 / 27.4 / 24.0 at 40 / 45 / 50 °C.
    rows.push({ name: 'Keq (derived from Kf/Kb)', lower: 0, value: ex.system === 'meoac' ? 30.2 : 60.0, upper: 200 });
  }
  return rows;
};

// ═══════════════════════════════════════════════════════════════════════════
// Dependency-free SVG chart. Colours and sizing come from HelpExamples.css.
// ═══════════════════════════════════════════════════════════════════════════
const MiniChart = ({ seriesList, xLabel, yLabel, height = 300 }) => {
  const W = 640, H = height, pad = { l: 58, r: 16, t: 26, b: 34 };
  const allX = seriesList.flatMap(s => s.points.map(p => p.x)).filter(Number.isFinite);
  const allY = seriesList.flatMap(s => s.points.map(p => p.y)).filter(Number.isFinite);
  if (allX.length === 0) return null;

  // No forced zero-origin: PFR product fractions are ~1e-3 against a 0.84
  // inert background, and a 0-to-max range flattened them onto the baseline.
  const xLo = Math.min(...allX), xHi = Math.max(...allX);
  const yLo = Math.min(...allY), yHi = Math.max(...allY);
  const xPad = (xHi - xLo) * 0.06 || 1;
  const yPad = (yHi - yLo) * 0.12 || (yHi * 0.12) || 1;
  const xMin = xLo - xPad, xMax = xHi + xPad;
  const yMin = Math.max(0, yLo - yPad), yMax = yHi + yPad;

  const sx = x => pad.l + (x - xMin) / (xMax - xMin) * (W - pad.l - pad.r);
  const sy = y => H - pad.b - (y - yMin) / (yMax - yMin) * (H - pad.t - pad.b);
  const fmtY = v => (yMax < 0.02 ? v.toExponential(1) : v.toFixed(3));
  const clean = pts => pts.filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));

  return (
    <svg className="hx-chart" viewBox={`0 0 ${W} ${H}`} style={{ height }} role="img"
      aria-label={`${yLabel} versus ${xLabel}`}>
      {Array.from({ length: 6 }, (_, i) => {
        const y = yMin + (yMax - yMin) * i / 5;
        return (
          <g key={`gy-${i}`}>
            <line className="hx-chart-grid" x1={pad.l} x2={W - pad.r} y1={sy(y)} y2={sy(y)} />
            <text className="hx-chart-tick" x={pad.l - 6} y={sy(y) + 3} textAnchor="end">{fmtY(y)}</text>
          </g>
        );
      })}
      {Array.from({ length: 6 }, (_, i) => {
        const x = xMin + (xMax - xMin) * i / 5;
        return (
          <g key={`gx-${i}`}>
            <line className="hx-chart-grid hx-chart-grid--v" x1={sx(x)} x2={sx(x)} y1={pad.t} y2={H - pad.b} />
            <text className="hx-chart-tick" x={sx(x)} y={H - pad.b + 14} textAnchor="middle">{x.toFixed(0)}</text>
          </g>
        );
      })}
      <line className="hx-chart-axis" x1={pad.l} x2={W - pad.r} y1={H - pad.b} y2={H - pad.b} />
      <line className="hx-chart-axis" x1={pad.l} x2={pad.l} y1={pad.t} y2={H - pad.b} />
      <text className="hx-chart-axislabel" x={(W + pad.l - pad.r) / 2} y={H - 4} textAnchor="middle">{xLabel}</text>
      <text className="hx-chart-axislabel" x={12} y={(pad.t + H - pad.b) / 2} textAnchor="middle"
        transform={`rotate(-90 12 ${(pad.t + H - pad.b) / 2})`}>{yLabel}</text>

      {seriesList.map((s, si) => (
        <g key={si} style={{ '--hx-series': s.color }}>
          <polyline className="hx-chart-line" stroke={s.color}
            points={clean(s.points).map(p => `${sx(p.x)},${sy(p.y)}`).join(' ')} />
          {clean(s.points).map((p, pi) => (
            <circle key={pi} cx={sx(p.x)} cy={sy(p.y)} r="3" fill={s.color} />
          ))}
        </g>
      ))}
      {seriesList.map((s, si) => (
        <g key={`lg-${si}`} transform={`translate(${pad.l + (si % 4) * 118}, ${8 + Math.floor(si / 4) * 12})`}>
          <circle cx="4" cy="0" r="3" fill={s.color} />
          <text className="hx-chart-legend" x="10" y="3">{s.name}</text>
        </g>
      ))}
    </svg>
  );
};

// ─── Local UI helpers ───────────────────────────────────────────────────────
const Panel = ({ title, hint, children, className }) => (
  <section className={cx('hx-panel', className)}>
    {title && (
      <h3 className="hx-panel-title">
        {title}{hint && <span className="hx-panel-hint"> {hint}</span>}
      </h3>
    )}
    {children}
  </section>
);

const RxToggle = ({ label, value, onChange, options, disabled }) => (
  <div className="hx-field">
    <div className="hx-field-label">{label}</div>
    <div className="hx-toggle" role="group" aria-label={label}>
      {options.map(opt => (
        <button key={opt.value} type="button" disabled={disabled}
          aria-pressed={value === opt.value}
          className={cx('hx-toggle-btn', value === opt.value && 'hx-toggle-btn--on')}
          onClick={() => !disabled && onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const RxField = ({ label, value, onChange, unit, step = 0.1, type = 'number', disabled }) => (
  <div className="hx-field">
    <label className="hx-field-label">{label}</label>
    <div className={cx('hx-input-wrap', disabled && 'hx-input-wrap--disabled')}>
      <input className="hx-input" type={type} step={step} value={value} disabled={disabled}
        aria-label={label} onChange={e => onChange(e.target.value)} />
      {unit && <span className="hx-input-unit">{unit}</span>}
    </div>
  </div>
);

// Provenance badge — published measurement vs computed from a published model.
const SourceTag = ({ source }) => {
  const label = source === 'published' ? 'Published data'
    : source === 'digitized' ? 'Digitized / calculated'
      : 'Model-generated';
  return (
    <span className={cx('hx-tag', source === 'published' ? 'hx-tag--published' : 'hx-tag--derived')}>
      {label}
    </span>
  );
};

const DataTable = ({ ds, size = '' }) => (
  <div className={cx('hx-tablewrap', size && `hx-tablewrap--${size}`)}>
    <table className="hx-table">
      <thead>
        <tr>
          <th className="hx-td-check" scope="col"><span className="hx-sr">Use</span></th>
          {ds.displayCols.map((col, i) => <th key={i} scope="col">{col}</th>)}
        </tr>
      </thead>
      <tbody>
        {ds.rows.map((row, ri) => (
          <tr key={ri}>
            <td className="hx-td-check"><input type="checkbox" checked disabled readOnly /></td>
            {row.map((val, ci) => <td key={ci}>{val}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════

const MAYMAN_STAGE1_STORAGE_PREFIX = 'kinetiq_mayman_stage1_runlog_v1';
const maymanStage1StorageKey = () => {
  try {
    const account = (localStorage.getItem('email') || 'anonymous').trim().toLowerCase();
    return `${MAYMAN_STAGE1_STORAGE_PREFIX}:${encodeURIComponent(account)}`;
  } catch {
    return `${MAYMAN_STAGE1_STORAGE_PREFIX}:session`;
  }
};
const maymanStage1RecordKey = (pressureGroupKey, reversibility, modelType) =>
  `${pressureGroupKey}::${reversibility}::${modelType}`;
const finiteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const HelpExamples = () => {
  const [activeSystem, setActiveSystem] = useState('meoac');
  const [selectedId, setSelectedId]     = useState(null);
  const [runStatus, setRunStatus] = useState('idle'); // idle | running | done | timeout | error
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError]   = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerRef = useRef(null);

  const [rxCond, setRxCond] = useState(null);
  const setRxField = (key) => (val) => setRxCond(prev => ({ ...(prev || {}), [key]: val }));

  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  const [wizardStep, setWizardStep] = useState('data'); // data | model | graph | params
  const [isLocked, setIsLocked] = useState(true);
  const [activeDsIdx, setActiveDsIdx] = useState(0);
  // Stage 1 results are deliberately recorded per pressure + reversible model
  // on this browser so the six runs can be compared after returning to the list.
  // Only compact metrics/parameter summaries are stored; plots and CSVs are not.
  const [maymanStage1Records, setMaymanStage1Records] = useState(() => {
    try { return JSON.parse(localStorage.getItem(maymanStage1StorageKey()) || '{}') || {}; }
    catch { return {}; }
  });

  const saveMaymanStage1Record = (record) => {
    setMaymanStage1Records((previous) => {
      const next = { ...previous, [record.key]: record };
      try { localStorage.setItem(maymanStage1StorageKey(), JSON.stringify(next)); } catch { /* best-effort browser storage */ }
      return next;
    });
  };

  const recordMaymanStage1Response = (ex, dataset, responseData, elapsedSeconds) => {
    if (!ex?.pressureGroupKey || !responseData) return;
    const metrics = responseData.metrics || {};
    const r2 = {
      ...(metrics.r2_summary || {}),
      ...(metrics.r2_per_species || {}),
      ...(metrics.per_species_r2 || {}),
    };
    const overallR2 = finiteNumberOrNull(metrics.overall_r2 ?? metrics.final_r2 ?? responseData.overall_r2);
    const stopReason = String(responseData.stop_reason ?? responseData.bo_stop_reason ?? metrics.stop_reason ?? 'not reported');
    const note = String(responseData.message ?? responseData.failure_message ?? responseData.error ?? '');
    const timeout = /time.?out|maximum runtime|timed out/i.test(`${stopReason} ${note}`);
    const stopped = responseData.stopped === true || /user_stopped|cancel/i.test(stopReason);
    const failure = Boolean(responseData.error) || responseData.failed_repeatedly === true;
    const rawParameters = metrics.reaction_params
      || responseData.reaction_params
      || responseData.fit_results
      || responseData.fitted_params
      || [];
    const compactParameters = Array.isArray(rawParameters)
      ? rawParameters.map((parameter, index) => ({
          reaction: parameter?.reaction_index ?? index + 1,
          Af: finiteNumberOrNull(parameter?.Af ?? parameter?.Af_aspen),
          Ea_f_kJ_mol: finiteNumberOrNull(parameter?.Ea_kJ_mol ?? parameter?.Ea_f_kJ_mol ?? parameter?.Ea_f),
          Ab: finiteNumberOrNull(parameter?.Ab ?? parameter?.Ab_aspen),
          Ea_b_kJ_mol: finiteNumberOrNull(parameter?.Ea_rev_kJ_mol ?? parameter?.Ea_b_kJ_mol ?? parameter?.Ea_b),
          model_type: parameter?.model_type ?? ex.modelType,
        }))
      : rawParameters;
    const record = {
      key: maymanStage1RecordKey(ex.pressureGroupKey, ex.reversibility, ex.modelType),
      exampleId: ex.id,
      pressureGroupKey: ex.pressureGroupKey,
      pressureLabel: dataset?.meta?.pressureLabel || ex.pressureLabel || ex.pressureCase?.label || '',
      pressurePsiAbs: finiteNumberOrNull(dataset?.meta?.P_psi_abs ?? ex.pressureCase?.pressurePsiAbs),
      pointRange: dataset?.meta?.pointRange || ex.pointRange || '',
      pointCount: dataset?.rows?.length || 0,
      reversibility: ex.reversibility,
      modelType: ex.modelType,
      status: timeout ? 'Timed out — best-so-far result' : stopped ? 'Stopped' : failure ? 'Failed' : 'Response received',
      stopReason,
      note,
      overallR2,
      perSpeciesR2: r2,
      objectiveLoss: finiteNumberOrNull(metrics.loss ?? metrics.objective_loss ?? responseData.loss ?? responseData.best_loss),
      fittedParameters: compactParameters,
      lhhwKiValues: responseData.lhhw_ki_values ?? metrics.lhhw_ki_values ?? null,
      iterations: finiteNumberOrNull(responseData.n_iter_actual ?? responseData.bo_iters_run ?? metrics.n_iter_actual),
      elapsedSeconds: finiteNumberOrNull(elapsedSeconds),
      savedAt: new Date().toISOString(),
    };
    saveMaymanStage1Record(record);
  };

  const clearMaymanStage1Records = () => {
    setMaymanStage1Records({});
    try { localStorage.removeItem(maymanStage1StorageKey()); } catch { /* ignore */ }
  };

  const downloadMaymanStage1Records = () => {
    const csvCell = (value) => {
      const text = value == null ? '' : (typeof value === 'string' ? value : JSON.stringify(value));
      return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const headers = [
      'Pressure group', 'Absolute pressure (psia)', 'Workbook points', 'Point count', 'Reaction type',
      'Model', 'Status', 'Overall R2', 'Per-species R2 JSON', 'Objective loss',
      'Iterations', 'Runtime (s)', 'Fitted parameters JSON', 'LHHW Ki JSON', 'Stop reason', 'Message', 'Last run (ISO)',
    ];
    const rows = MAYMAN_PRESSURE_CASES.flatMap((pressureCase) => [
      { modelType: 'powerlaw', label: 'Reversible Power Law' },
      { modelType: 'lhhw', label: 'Reversible LHHW' },
    ].map((model) => {
      const r = maymanStage1Records[maymanStage1RecordKey(pressureCase.key, 'reversible', model.modelType)] || {};
      return [
        pressureCase.label, pressureCase.pressurePsiAbs.toFixed(3), pressureCase.pointRange, pressureCase.pointCount,
        'reversible', model.label, r.status || 'Not run yet', r.overallR2, r.perSpeciesR2 || {}, r.objectiveLoss,
        r.iterations, r.elapsedSeconds, r.fittedParameters || [], r.lhhwKiValues || {}, r.stopReason || '',
        r.note || '', r.savedAt || '',
      ];
    }));
    const csv = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Mayman_Stage1_Run_Results.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (rid) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
        const res = await fetch(`${API}/api/run/progress/${rid}`, {
          headers: token ? { Authorization: token } : {},
        });
        if (!res.ok) return;
        const body = await res.json();
        setProgress(body);
        if (body.phase === 'done' || body.phase === 'error') stopPolling();
      } catch {
        // transient poll failure — next tick retries; not fatal to the run
      }
    }, 800);
  };

  useEffect(() => () => stopPolling(), []);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // 23-Oct-2026 FIX (Madhura, 21-Oct call ~52:48): while an example runs, show the
  // SAME wording as the Data Fitting page's Run button - phase + iteration
  // counter - and no elapsed seconds, so both screens read identically.
  const runIterTotal = progress?.n_iter || 100;
  const runIterDone  = progress?.iter_done ?? 0;
  const runningLabel = (() => {
    const p = progress?.phase;
    if (!p || p === 'unknown') return `Waiting for optimizer progress\u2026 iter 0/${runIterTotal}`;
    if (p === 'validating')    return `Validating input\u2026 iter 0/${runIterTotal}`;
    if (p === 'initializing')  return `Preparing optimizer\u2026 iter ${runIterDone}/${runIterTotal}`;
    if (p === 'optimizing')    return `Running optimization\u2026 iter ${runIterDone}/${runIterTotal}`;
    if (p === 'plotting')      return `Preparing results\u2026 iter ${progress?.iter_done ?? runIterTotal}/${runIterTotal}`;
    if (p === 'done')          return `Completed\u2026 iter ${progress?.iter_done ?? runIterTotal}/${runIterTotal}`;
    return 'Running\u2026';
  })();

  const sysMeta         = SYSTEM_META[activeSystem];
  const sysExamples     = EXAMPLES.filter(e => e.system === activeSystem);
  const selectedExample = EXAMPLES.find(e => e.id === selectedId) || null;
  const activeGroup     = REACTOR_GROUPS.find(g => g.systems.includes(activeSystem)) || REACTOR_GROUPS[0];

  const resetRunState = () => {
    setRunStatus('idle'); setRunResult(null); setRunError('');
    setProgress(null); stopPolling();
    setWizardStep('data'); setIsLocked(true); setActiveDsIdx(0);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleSystemTabClick = (sKey) => {
    setActiveSystem(sKey); setSelectedId(null); setRxCond(null); resetRunState();
  };

  const handleGroupTabClick = (gKey) => {
    const group = REACTOR_GROUPS.find(g => g.key === gKey);
    if (group) handleSystemTabClick(group.systems[0]);
  };

  const openExample = (ex) => {
    setSelectedId(ex.id);
    resetRunState();
    setRxCond({
      reactorMode: ex.reactorMode || 'batch',
      reactionPhase: ex.reactionPhase || 'liquid',
      concentrationBasis: ex.concentrationBasis || 'mole_fraction',
      rateBasis: ex.rateBasis || 'cat_weight',
      wCatG: ex.wCatG || '',
      vRxL: ex.vRxL || '0.25',
      nMixMol: ex.nMixMol || '',
      reactionTempC: ex.reactionTempC || '',
      reactionPressure: ex.reactionPressure || '1',
      pressureUnit: ex.pressureUnit || 'atm',
      reversibility: ex.reversibility,
      modelType: ex.modelType,
      exponentMode: ex.exponentMode,
      keqMode: ex.keqMode,
    });
  };

  const backToList = () => { setSelectedId(null); resetRunState(); setRxCond(null); };

  // Emits `csvHeader` (names the backend parses), NOT `displayCols` (readable
  // labels). Sending displayCols is what broke every PFR run.
  const toCsvFile = (ds, name) => {
    const header = ds.csvHeader || ds.displayCols;
    const text = [header.join(','), ...ds.rows.map(r => r.join(','))].join('\n');
    return new File([new Blob([text], { type: 'text/csv' })], name, { type: 'text/csv' });
  };

  // Routes per system AND per fit scope. Previously everything went to
  // /api/run/data-fitting, wrong for AA and wrong for every multi-dataset case.
  const runRegression = async (ex) => {
    const datasets = datasetsFor(ex);
    if (!datasets.length) {
      setRunError('No reference data is defined for this example.');
      setRunStatus('error');
      return;
    }

    const cond = rxCond || {};
    const isMulti = ex.fitScope === 'multiple';
    const isMayman = ex.system === 'mayman_2018';
    const isGenericPfr = isMayman;

    // Fail locally with a useful diagnostic if the built-in PFR example's CSV
    // header/row shape drifts from the generic backend's canonical contract.
    if (isMayman) {
      const maymanDataset = datasets[0];
      const header = maymanDataset?.csvHeader || [];
      const missingHeaders = MAYMAN_PFR_CSV_HEADERS.filter((name) => !header.includes(name));
      const badRow = maymanDataset?.rows?.findIndex((row) => row.length !== header.length) ?? -1;
      const expectedPsi = ex.pressureCase?.pressurePsiAbs;
      const wrongPressureRow = expectedPsi == null ? -1 : (maymanDataset?.rows || []).findIndex(
        row => Math.abs(Number(row[0]) - expectedPsi) > 0.001
      );
      if (missingHeaders.length || header.length !== MAYMAN_PFR_CSV_HEADERS.length || badRow >= 0
          || maymanDataset?.rows?.length !== ex.pressureCase?.pointCount || wrongPressureRow >= 0) {
        const details = [
          missingHeaders.length ? `missing headers: ${missingHeaders.join(', ')}` : '',
          header.length !== MAYMAN_PFR_CSV_HEADERS.length ? `expected ${MAYMAN_PFR_CSV_HEADERS.length} columns, found ${header.length}` : '',
          badRow >= 0 ? `row ${badRow + 1} has ${maymanDataset.rows[badRow].length} values for ${header.length} headers` : '',
          maymanDataset?.rows?.length !== ex.pressureCase?.pointCount ? `expected ${ex.pressureCase?.pointCount} points for ${ex.pressureCase?.label}, found ${maymanDataset?.rows?.length}` : '',
          wrongPressureRow >= 0 ? `row ${wrongPressureRow + 1} does not match ${ex.pressureCase?.pressurePsiAbs} psia` : '',
        ].filter(Boolean).join('; ');
        setRunError(`Built-in Mayman PFR dataset validation failed (${details}). This card must contain one pressure group only.`);
        setRunStatus('error');
        return;
      }
    }

    setRunStatus('running'); setRunResult(null); setRunError(''); setProgress(null);
    const startedAt = Date.now();
    setElapsedMs(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 250);

    const rid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `run_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    startPolling(rid);

    try {
      const bundle = EXAMPLE_DATASETS[ex.system];
      const speciesWeights = {};
      (bundle.speciesForWeights || []).forEach(sp => { speciesWeights[sp] = 1; });

      const fd = new FormData();
      fd.append('run_id', rid);
      fd.append('reaction_system', ex.system);

      fd.append('reaction_type', cond.reversibility);   // aa-fitting field name
      fd.append('reversibility', cond.reversibility);   // data-fitting field name
      fd.append('model_type', cond.modelType);
      fd.append('exponents', cond.exponentMode === 'fixed_1' ? 'fixed' : 'free');
      fd.append('exponent_mode', cond.exponentMode);
      fd.append('keq_mode', cond.keqMode);
      if (!isGenericPfr) {
        // AA composes its own mode server-side from reaction_type +
        // exponents, so don't send a conflicting one.
        fd.append('mode', 'keq_literature');
        fd.append('fit_mode', 'keq_literature');
      }

      fd.append('reactor_mode', cond.reactorMode || 'batch');
      fd.append('reaction_phase', cond.reactionPhase || 'liquid');
      fd.append('concentration_basis', cond.concentrationBasis || 'mole_fraction');
      fd.append('rate_basis', cond.rateBasis || 'cat_weight');
      fd.append('W_cat_g', cond.wCatG || '');
      fd.append('w_cat_g', cond.wCatG || '');
      fd.append('V_rx_L', cond.vRxL || '');
      fd.append('v_rx_l', cond.vRxL || '');
      fd.append('optimize_exponents', String(cond.exponentMode !== 'fixed_1'));
      if (!isMayman) fd.append('species_weights', JSON.stringify(speciesWeights));
      fd.append('r2_target', isMayman ? '0.90' : '0.99');
      fd.append('n_iter', '100');
      fd.append('warm_start', 'false');
      // Batch charge is meaningless for a PFR; T is a CSV column there.
      if (!isGenericPfr) {
        fd.append('n_mix_mol', cond.nMixMol || '');
        if (cond.reactionTempC !== '' && cond.reactionTempC != null) {
          fd.append('T_K', String(parseFloat(cond.reactionTempC) + 273.15));
        }
      }
      fd.append('pressure', cond.reactionPressure || '1');
      fd.append('pressure_unit', cond.pressureUnit || 'atm');
      fd.append('reaction_orders', JSON.stringify([{ reactants: {}, products: {} }]));

      if (isGenericPfr) {
        // Mayman is routed through the generic PFR engine. The source's
        // CO and CO₂ outlet fractions are aggregated as COx to follow Fig. 1;
        // reaction 3 uses Madhura's CO-based 2.5 O₂ stoichiometry as a documented approximation.
        // This is not a reproduction of the paper's full 10-rate mechanism.
        const reversible = cond.reversibility === 'reversible';
        // Use the catalogue's canonical species_key values for real chemical
        // species. The embedded Mayman CSV uses these exact keys for every
        // <species>In/<species>Out column, so header normalization does not
        // depend on whether a database alias (e.g. CH3COOH) is configured.
        // Sending formula-only identity rows here as well creates duplicate
        // metadata matches and can cause species weights (especially the
        // zero-weight O2/H2O) not to reach the canonical objective keys. COx
        // is the one intentional pseudo-species, declared explicitly below.
        fd.append('stoichiometry', JSON.stringify([
          { reactants: 'ethane_c2h6 + 0.5 oxygen_o2', products: 'ethene_c2h4 + water_h2o', reversible },
          { reactants: 'ethane_c2h6 + 1.5 oxygen_o2', products: 'ethanoic_acid_c2h4o2 + water_h2o', reversible },
          { reactants: 'ethane_c2h6 + 2.5 oxygen_o2', products: '2 COx + 3 water_h2o', reversible },
        ]));
        fd.set('reactor_mode', 'pfr');
        fd.set('concentration_basis', 'mole_fraction');
        fd.set('rate_basis', 'cat_weight');
        fd.set('W_cat_g', String(MAYMAN_CATALYST_G));
        fd.set('V_rx_L', String(MAYMAN_REACTOR_VOLUME_L));
        fd.set('reaction_temp_c', '225');
        fd.set('pressure', String(datasets[0]?.meta?.P_psi_abs ?? ex.pressureCase?.pressurePsiAbs ?? cond.reactionPressure ?? '214.693'));
        fd.set('pressure_unit', 'psi');
        fd.set('inert_components', JSON.stringify([]));
        fd.append('component_names', JSON.stringify({
          ethane_c2h6: 'Ethane',
          oxygen_o2: 'Oxygen',
          ethene_c2h4: 'Ethene',
          water_h2o: 'Water',
          ethanoic_acid_c2h4o2: 'Acetic acid',
          COx: 'Combined CO + CO₂ (lumped)',
        }));
        fd.append('species_identity', JSON.stringify([
          { species_key: 'COx', species_name: 'Combined CO + CO2 (lumped)', formula: '', aliases: ['COx', 'CO+CO2', 'CO + CO2'] },
        ]));
        fd.set('species_weights', JSON.stringify({
          ethane_c2h6: 1, ethene_c2h4: 1, ethanoic_acid_c2h4o2: 1,
          COx: 1, oxygen_o2: 0, water_h2o: 0,
        }));
        if (cond.modelType === 'lhhw') {
          fd.set('lhhw_ki_values', JSON.stringify({
            ethane_c2h6: 1, oxygen_o2: 1, ethene_c2h4: 1,
            water_h2o: 1, ethanoic_acid_c2h4o2: 1, COx: 1,
          }));
        }
      }

      let url;
      if (isMulti) {
        datasets.forEach((ds, i) => {
          fd.append('files[]', toCsvFile(ds, `${ex.system}_example_${i + 1}.csv`));
        });
        fd.append('run_meta', JSON.stringify(datasets.map(ds => ({
          label:     ds.label,
          T_C:       ds.meta?.T_C,
          n_mix_mol: ds.meta?.n_mix_mol,
          W_cat_g:   ds.meta?.W_cat_g,
          weight:    1.0,
        }))));
        url = ex.system === 'dme'
          ? `${API}/api/run/dme-global-fitting`
          : `${API}/api/run/meoac-global-fitting`;
      } else {
        fd.append('file', toCsvFile(datasets[0], `${ex.system}_example.csv`));
        url = isGenericPfr
          ? `${API}/api/run/generic-fitting`
          : `${API}/api/run/data-fitting`;
      }

      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
      const res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: token } : {},
        body: fd,
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      clearInterval(timerRef.current);
      stopPolling();
      setRunResult(data);
      const stopReason = String(data.stop_reason ?? data.bo_stop_reason ?? data.metrics?.stop_reason ?? '');
      const timedOutWithBestSoFar = isGenericPfr && /time.?out|maximum runtime|timed out/i.test(stopReason);
      setRunStatus(timedOutWithBestSoFar ? 'timeout' : 'done');
      if (isMayman) recordMaymanStage1Response(ex, datasets[0], data, (Date.now() - startedAt) / 1000);
    } catch (err) {
      clearInterval(timerRef.current);
      stopPolling();
      setRunError(err.message || 'Fitting failed');
      setRunStatus('error');
      if (ex.system === 'mayman_2018') {
        recordMaymanStage1Response(ex, datasets[0], { error: err.message || 'Fitting failed' }, (Date.now() - startedAt) / 1000);
      }
    }
  };

  const sysVar = { '--hx-sys': sysMeta.color };

  return (
    <div className="hx-root" style={sysVar}>
      <Sidebar />
      <div className="hx-main">
        <Navbar activePage="app" />

        <div className="hx-scroll">
          <div className="hx-container">

            {/* ── Header ── */}
            <header className="hx-header">
              <div className="hx-header-row">
                <span className="hx-title">📖 Examples</span>
                <span className="hx-badge">✎ Editable reference</span>
              </div>
              {!selectedExample && (
                // [MK 09-Sep] "Edit this text to be more concise as follows" —
                // her replacement wording, used verbatim.
                <p className="hx-intro">
                  These locked reference systems show completed examples as a guideline. To start a
                  new system with an example system as a starting point, go to{' '}
                  <Link to="/data-fitting#input" className="hx-link">Data Fitting → Input</Link>{' '}
                  and choose <strong>✦ Initialize with Example</strong> to copy the values into the
                  live form.
                </p>
              )}
            </header>

            {!selectedExample && (
              <>
                <nav className="hx-tabs" aria-label="Reactor type">
                  {REACTOR_GROUPS.map(g => (
                    <button key={g.key} type="button"
                      className={cx('hx-tab', activeGroup.key === g.key && 'hx-tab--active')}
                      aria-current={activeGroup.key === g.key ? 'page' : undefined}
                      onClick={() => handleGroupTabClick(g.key)}>
                      {g.label}
                    </button>
                  ))}
                </nav>

                {activeGroup.systems.length > 1 && (
                  <div className="hx-chips" role="group" aria-label="Chemical system">
                    {activeGroup.systems.map(sKey => {
                      const sm = SYSTEM_META[sKey];
                      const active = activeSystem === sKey;
                      return (
                        <button key={sKey} type="button" style={{ '--hx-sys': sm.color }}
                          className={cx('hx-chip', active && 'hx-chip--active')}
                          aria-pressed={active}
                          onClick={() => handleSystemTabClick(sKey)}>
                          {sm.displayName}
                          <span className="hx-chip-tag">{sm.shortName}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* [MK 09-Sep] Full name as heading, reaction in text
                    underneath, formula demoted to a chip. Citation renders
                    only when showReference — never for Acetic Acid. */}
                <div className="hx-sysbar">
                  <div className="hx-sysbar-row">
                    <div className="hx-sysbar-names">
                      <div className="hx-sysbar-name">{sysMeta.displayName}</div>
                      <div className="hx-sysbar-sub">{sysMeta.subtitle}</div>
                    </div>
                    <div className="hx-eq">{sysMeta.equation}</div>
                    {sysMeta.showReference && sysMeta.reference && (
                      <div className="hx-ref">
                        📚 {sysMeta.reference}
                        {sysMeta.doi && <><br /><span className="hx-ref-doi">DOI: {sysMeta.doi}</span></>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Two-up card grid — was a single tall stack. */}
                <div className="hx-cards">
                  {sysExamples.map(ex => {
                    const mm = MODEL_KEY_META[ex.modelKey];
                    const eq = MODEL_EQUATIONS[ex.system]?.[ex.modelKey];
                    const ds = datasetsFor(ex);
                    const pts = ds.reduce((n, d) => n + d.rows.length, 0);
                    return (
                      <button key={ex.id} type="button" className="hx-card"
                        onClick={() => openExample(ex)}>
                        <div className="hx-card-head">
                          <span className="hx-card-name">
                            <span className="hx-card-icon" aria-hidden="true">{mm?.icon}</span>
                            {ex.label}
                          </span>
                          <span className="hx-tag hx-tag--scope">{mm?.tag}</span>
                        </div>

                        {/* [MK 09-Sep] headline + detail underneath */}
                        <p className="hx-card-desc">{ex.description}</p>
                        <p className="hx-card-detail">{ex.detail}</p>

                        {eq && (
                          <div className="hx-card-rate">
                            <span className="hx-card-rate-label">Rate</span>{eq}
                          </div>
                        )}

                        <div className="hx-taglist">
                          <SourceTag source={ds.every(d => d.source === 'published') ? 'published' : (ds.some(d => d.source === 'digitized') ? 'digitized' : 'derived')} />
                          {ex.reviewStatus && <span className="hx-tag hx-tag--warning">{ex.reviewStatus}</span>}
                          {[
                            ex.reactorMode?.toUpperCase(),
                            ex.reactionPhase,
                            ex.reversibility === 'reversible' ? '⇌ Reversible' : '→ Irreversible',
                            ds.length > 1 ? `${ds.length} data sets · ${pts} pts` : `${pts} points`,
                            ex.reactionTempC ? `T = ${ex.reactionTempC} °C` : (ds[0]?.meta?.T_range_C ? `T = ${ds[0].meta.T_range_C} °C` : (ds[0]?.meta?.T_C != null ? `T = ${ds[0].meta.T_C} °C` : null)),
                            ds[0]?.meta?.pressureLabel ? `P = ${ds[0].meta.pressureLabel}` : (ds[0]?.meta?.P_range_psi ? `P = ${ds[0].meta.P_range_psi}` : (ds[0]?.meta?.P_psi != null ? `P = ${ds[0].meta.P_psi} psi` : null)),
                            ex.wCatG ? `Catalyst = ${ex.wCatG} g` : null,
                          ].filter(Boolean).map((tag, i) => (
                            <span key={i} className="hx-tag">{tag}</span>
                          ))}
                        </div>
                        <div className="hx-card-open">Open this example</div>
                      </button>
                    );
                  })}
                </div>

                {activeSystem === 'mayman_2018' && (
                  <Panel title="Stage 1 pressure-specific run log" hint="(saved locally in this browser)">
                    <p className="hx-body hx-body--dim">
                      Run the reversible Power Law and reversible LHHW cards independently for each pressure. The latest response for each of those six combinations is recorded here and survives returning to the list or refreshing this browser. A timeout is recorded as best-so-far, not convergence. The global three-dataset fit is intentionally deferred.
                    </p>
                    <div className="hx-tablewrap">
                      <table className="hx-table">
                        <thead>
                          <tr>
                            {['Pressure group', 'Workbook points', 'Model', 'Status', 'Overall R²', 'C₂H₆ R²', 'C₂H₄ R²', 'Acetic acid R²', 'COx R²', 'Iterations', 'Runtime (s)', 'Last run'].map(h => (
                              <th key={h} scope="col">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {MAYMAN_PRESSURE_CASES.flatMap(pressureCase => [
                            { modelType: 'powerlaw', label: 'Reversible Power Law' },
                            { modelType: 'lhhw', label: 'Reversible LHHW' },
                          ].map(model => {
                            const key = maymanStage1RecordKey(pressureCase.key, 'reversible', model.modelType);
                            const r = maymanStage1Records[key];
                            const speciesR2 = r?.perSpeciesR2 || {};
                            const firstMetric = (...keys) => {
                              for (const metricKey of keys) {
                                const value = finiteNumberOrNull(speciesR2[metricKey]);
                                if (value != null) return value.toFixed(4);
                              }
                              return '—';
                            };
                            return (
                              <tr key={key}>
                                <td>{pressureCase.label} ({pressureCase.pressurePsiAbs.toFixed(3)} psia)</td>
                                <td>{pressureCase.pointRange} ({pressureCase.pointCount})</td>
                                <td>{model.label}</td>
                                <td>{r?.status || 'Not run yet'}{r?.stopReason && r.status !== 'Response received' ? ` · ${r.stopReason}` : ''}</td>
                                <td>{r?.overallR2 != null ? r.overallR2.toFixed(4) : '—'}</td>
                                <td>{firstMetric('ethane_c2h6', 'C2H6', 'C₂H₆')}</td>
                                <td>{firstMetric('ethene_c2h4', 'C2H4', 'C₂H₄')}</td>
                                <td>{firstMetric('ethanoic_acid_c2h4o2', 'CH3COOH', 'Acetic acid')}</td>
                                <td>{firstMetric('COx', 'coX', 'co_x')}</td>
                                <td>{r?.iterations ?? '—'}</td>
                                <td>{r?.elapsedSeconds != null ? r.elapsedSeconds.toFixed(1) : '—'}</td>
                                <td>{r?.savedAt ? new Date(r.savedAt).toLocaleString() : '—'}</td>
                              </tr>
                            );
                          }))}
                        </tbody>
                      </table>
                    </div>
                    <div className="hx-actions hx-actions--end">
                      <button type="button" className="hx-btn hx-btn--ghost" onClick={downloadMaymanStage1Records}>
                        Download Stage 1 results CSV
                      </button>
                      <button type="button" className="hx-btn hx-btn--ghost" onClick={clearMaymanStage1Records}>
                        Clear Stage 1 run log
                      </button>
                    </div>
                  </Panel>
                )}
              </>
            )}

            {/* ═════════════════ REGRESSION WORKSPACE ═════════════════ */}
            {selectedExample && (() => {
              const ex = selectedExample;
              const datasets = datasetsFor(ex);
              const isMulti = ex.fitScope === 'multiple';
              const isMayman = ex.system === 'mayman_2018';
              const isGenericPfr = isMayman;
              const hasFitResponse = runStatus === 'done' || runStatus === 'timeout';
              const shownDs = datasets[Math.min(activeDsIdx, datasets.length - 1)];

              // [FIX 15-Sep-2026] Initial Estimates used to be built from the
              // LOCKED ex.*, so unlocking and flipping Model / Reaction Type /
              // Exponents changed what was posted to the backend but left this
              // table showing the original set — the two silently disagreed.
              // Built from live rxCond now. This is also what makes Madhura's
              // reverse-exponent requirement reachable at all.
              const liveEx = { ...ex, ...(rxCond || {}) };
              const estimates = buildInitialEstimates(liveEx);
              const diverged = rxCond && (
                rxCond.modelType !== ex.modelType ||
                rxCond.reversibility !== ex.reversibility ||
                rxCond.exponentMode !== ex.exponentMode
              );

              const reactants1 = parseSpeciesNames(ex.rxn1Reactants);
              const products1  = parseSpeciesNames(ex.rxn1Products);
              const allCompounds = [...new Set([
                ...reactants1, ...products1,
                ...parseSpeciesNames(ex.rxn2Reactants), ...parseSpeciesNames(ex.rxn2Products),
                ...parseSpeciesNames(ex.rxn3Reactants), ...parseSpeciesNames(ex.rxn3Products),
                ...(ex.inerts ? [ex.inerts] : []),
              ])];
              const eq = MODEL_EQUATIONS[ex.system]?.[ex.modelKey];

              // The model-specific result readers below keep the gallery aligned
              // with the backend response returned by each currently shipped example.
              const fr = runResult?.fit_results?.[0] || {};
              const reactionParams = runResult?.metrics?.reaction_params || runResult?.reaction_params || [];
              const aaRxn1 = reactionParams[0] || {};
              const isMultiReactionExample = isMayman;
              // [FIX 26-Oct-2026] Multi-dataset (global) fits don't return
              // fit_results[0]; they put the shared Af/Ea/Ab/Ea_b under
              // global_metrics.aspen_params_ref_run0, aspen_ready or
              // global_metrics.kinetic_params. Reading only fr.* left every value
              // blank ("Af: ?") on the Parameters tab even though the fit
              // finished. Same fallback chain DataFittingPage's results tiles use.
              const aspRun     = runResult?.aspen_ready || runResult?.metrics?.aspen_params || fr.aspen_ready || {};
              const aspGlobal  = runResult?.global_metrics?.aspen_params_ref_run0 || {};
              const kinParams  = runResult?.global_metrics?.kinetic_params || runResult?.metrics?.kinetic_params || {};
              const firstNum   = (...vals) => {
                for (const v of vals) {
                  if (v == null || v === '') continue;
                  const n = Number(v);
                  if (Number.isFinite(n)) return n;
                }
                return null;
              };
              const paramAf   = isMultiReactionExample ? firstNum(aaRxn1.Af)
                : firstNum(fr.Af_aspen, aspGlobal.Af_aspen, aspRun.Af_aspen, kinParams.Af_aspen, kinParams.Af);
              const paramEaF  = isMultiReactionExample ? firstNum(aaRxn1.Ea_kJ_mol)
                : firstNum(fr.Ea_f, aspGlobal.Ea_f_kJ, aspRun.Ea_f, kinParams.Ea_f_kJ, kinParams.Ea_f);
              // Preserve the existing AA display mapping; the generic Mayman
              // response carries Ab/Ea_b on each reaction entry.
              const paramAb   = isMayman ? firstNum(reactionParams[0]?.Ab)
                : firstNum(fr.Ab_internal, aspGlobal.Ab_aspen, aspRun.Ab_aspen, kinParams.Ab_aspen, kinParams.Ab);
              const paramEaB  = isMayman ? firstNum(reactionParams[0]?.Ea_rev_kJ_mol)
                : firstNum(fr.Ea_b, aspGlobal.Ea_b_kJ, aspRun.Ea_b, kinParams.Ea_b_kJ, kinParams.Ea_b);
              // R² can live under any of three keys depending on which
              // backend produced it (MeOAc/DME: r2_summary; AA:
              // r2_per_species; custom/generic: per_species_r2) — same merge
              // DataFittingPage.jsx's Graph tab uses for its "Fit Quality —
              // R²" sidebar box.
              const r2Summary = {
                ...(runResult?.metrics?.r2_summary     || {}),
                ...(runResult?.metrics?.r2_per_species || {}),
                ...(runResult?.metrics?.per_species_r2 || {}),
              };
              const displayR2Entries = Object.entries(r2Summary)
                .filter(([sp]) => !(isMayman && ['O2', 'H2O', 'OXYGEN_O2', 'WATER_H2O'].includes(String(sp).toUpperCase())));
              const mainPlot = runResult?.main_plot_base64 || runResult?.metrics?.main_plot_base64 || '';
              const perRunPlotEntries = Object.entries(runResult?.per_run_plots || {});
              const activeRunPlotEntry = isMulti
                ? (perRunPlotEntries.find(([key]) => key.startsWith(`run_${activeDsIdx + 1}_`))
                    || perRunPlotEntries[activeDsIdx] || null)
                : null;
              const activeRunPlot = activeRunPlotEntry?.[1] || '';
              const graphPlot = isMulti ? (activeRunPlot || mainPlot) : mainPlot;
              const graphTitle = isMayman
                ? 'Observed vs Predicted — Parity Overview (backend result)'
                : (isMulti && activeRunPlot
                  ? `Composition Profile — ${shownDs?.label || `Data Set ${activeDsIdx + 1}`} (backend result)`
                  : (isMulti
                    ? 'Combined Multi-Dataset Overview (backend result)'
                    : 'Composition Profile — Experimental Data + Model (backend result)'));

              // Song et al. (1998) Arrhenius expressions provide an apparent Ea
              // benchmark for LHHW multi-temperature fitting. Use the units from
              // the paper equations (cal/mol in the exponent) converted to kJ/mol.
              const SONG_LHHW_EA_BENCHMARK = {
                meoac: { equation: 'Eq. 21', kcal: 12.49, kJ: 52.26 },
                dme:   { equation: 'Eq. 25', kcal: 21.17, kJ: 88.58 },
              };
              const songEaBenchmark = isMulti && rxCond?.modelType === 'lhhw'
                ? SONG_LHHW_EA_BENCHMARK[ex.system] || null
                : null;
              const fittedEaRaw = runResult?.aspen_ready?.Ea_f
                ?? runResult?.global_metrics?.aspen_params_ref_run0?.Ea_f_kJ
                ?? runResult?.global_metrics?.kinetic_params?.Ea_f_kJ
                ?? runResult?.global_metrics?.kinetic_params?.Ea_f
                ?? runResult?.metrics?.kinetic_params?.Ea_f_kJ;
              const fittedEa = fittedEaRaw == null ? null : Number(fittedEaRaw);
              const fittedEaValid = fittedEa != null && Number.isFinite(fittedEa);
              const eaDelta = fittedEaValid && songEaBenchmark ? fittedEa - songEaBenchmark.kJ : null;
              const eaPercent = eaDelta != null && songEaBenchmark?.kJ
                ? 100 * eaDelta / songEaBenchmark.kJ : null;

              const DatasetTabs = () => isMulti ? (
                <div className="hx-dstabs" role="group" aria-label="Data set">
                  {datasets.map((ds, i) => (
                    <button key={i} type="button" aria-pressed={activeDsIdx === i}
                      className={cx('hx-dstab', activeDsIdx === i && 'hx-dstab--active')}
                      onClick={() => setActiveDsIdx(i)}>
                      {ds.label}
                    </button>
                  ))}
                </div>
              ) : null;

              return (
                <div>
                  <div className="hx-wshead">
                    <button type="button" className="hx-btn hx-btn--ghost" onClick={backToList}>
                      ← Back to list
                    </button>
                    <div>
                      <div className="hx-eyebrow">{sysMeta.displayName} — {sysMeta.subtitle}</div>
                      <div className="hx-wstitle">{ex.label}</div>
                      {ex.reviewStatus && <div className="hx-review-banner">{ex.reviewStatus}. Stage 1 independently fits workbook points 37–63 at 0 psig (14.696 psia) using converted outlet mole fractions with combined COx, 
                        whole °C temperatures, 4-decimal fractions, 
                        and PFR coordinate flow and volume assumptions rather than the full kinetic mechanism.</div>}
                    </div>
                  </div>

                  <nav className="hx-steps" aria-label="Example steps">
                    {[
                      { key: 'data',   label: '1 · Data Input',  reachable: true },
                      { key: 'model',  label: '2 · Model Input', reachable: true },
                      { key: 'graph',  label: '3 · Graph',       reachable: runStatus !== 'idle' },
                      { key: 'params', label: '4 · Parameters',  reachable: runStatus === 'done' || runStatus === 'timeout' },
                    ].map(step => (
                      <button key={step.key} type="button" disabled={!step.reachable}
                        aria-current={wizardStep === step.key ? 'step' : undefined}
                        className={cx('hx-step', wizardStep === step.key && 'hx-step--active')}
                        onClick={() => step.reachable && setWizardStep(step.key)}>
                        {step.label}
                      </button>
                    ))}
                  </nav>

                  {/* ── STEP 1 · DATA INPUT ── */}
                  {wizardStep === 'data' && (
                    <>
                      <div className="hx-2col">
                        <div className="hx-stack">
                          <Panel title="Information">
                            <p className="hx-body">{ex.description}</p>
                            <p className="hx-body hx-body--dim">{ex.detail}</p>
                          </Panel>

                          <Panel title="Reactions">
                            {[
                              [ex.rxn1Reactants, ex.rxn1Products],
                              [ex.rxn2Reactants, ex.rxn2Products],
                              [ex.rxn3Reactants, ex.rxn3Products],
                            ].filter(([r]) => r).map(([r, p], i) => (
                              <div key={i} className="hx-rxn">
                                <span className="hx-rxn-idx">R{i + 1}</span>
                                {r} {ex.reversibility === 'reversible' ? '⇌' : '→'} {p}
                              </div>
                            ))}
                            {ex.inerts && <p className="hx-body hx-body--dim">Inert: {ex.inerts}</p>}
                          </Panel>

                          <Panel title="Compounds">
                            <div className="hx-compounds">
                              {allCompounds.map((sp, i) => (
                                <span key={i} className="hx-compound">{sp}</span>
                              ))}
                            </div>
                          </Panel>
                        </div>

                        {/* [MK 09-Sep] "the data set should load automatically" —
                            single AND multi-dataset cases arrive with data in
                            the table; nothing asks for an upload. */}
                        <Panel
                          title="Experimental Data"
                          hint={`(loaded automatically · ${datasets.length === 1
                            ? `${datasets[0].rows.length} points`
                            : `${datasets.length} data sets`})`}>
                          <DatasetTabs />
                          <p className="hx-provenance">
                            <SourceTag source={shownDs.source} />{' '}
                            <cite>{shownDs.provenance}</cite>
                          </p>
                          <p className="hx-body hx-body--dim">
                            {isGenericPfr
                              ? 'Packed-bed PFR: one row per steady-state run, inlet and outlet composition; conditions vary by row.'
                              : 'Batch run: composition versus time.'}
                            {shownDs.meta?.W_cat_g ? ` · Catalyst ${shownDs.meta.W_cat_g} g` : ''}
                            {shownDs.meta?.T_range_C ? ` · ${shownDs.meta.T_range_C} °C` : (shownDs.meta?.T_C ? ` · ${shownDs.meta.T_C} °C` : '')}
                            {shownDs.meta?.pressureLabel ? ` · Pressure ${shownDs.meta.pressureLabel}` : ''}
                            {shownDs.meta?.pointRange ? ` · Workbook points ${shownDs.meta.pointRange}` : ''}
                          </p>
                          <DataTable ds={shownDs} size={isMulti ? 'short' : ''} />
                        </Panel>
                      </div>

                      <div className="hx-actions hx-actions--end">
                        <button type="button" className="hx-btn hx-btn--primary"
                          onClick={() => setWizardStep('model')}>
                          Next: Model Input →
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── STEP 2 · MODEL INPUT ── */}
                  {wizardStep === 'model' && (
                    <>
                      <div className={cx('hx-lock', !isLocked && 'hx-lock--open')}>
                        <span className="hx-lock-text">
                          {isLocked
                            ? '🔒 Locked — this is the reference setup for this example'
                            : '🔓 Unlocked — editing reference values'}
                        </span>
                        <button type="button" className="hx-btn hx-btn--ghost"
                          onClick={() => setIsLocked(l => !l)}>
                          {isLocked ? 'Unlock to edit' : 'Lock again'}
                        </button>
                      </div>

                      <div className="hx-2col">
                        <div className="hx-stack">
                          <Panel title="Thermodynamic / Kinetic Model">
                            {rxCond && (
                              <>
                                <div className="hx-row">
                                  <RxToggle disabled={isLocked} label="Model" value={rxCond.modelType}
                                    onChange={setRxField('modelType')}
                                    options={[{ value: 'powerlaw', label: 'Power Law' }, { value: 'lhhw', label: 'LHHW' }]} />
                                  <RxToggle disabled={isLocked} label="Reaction Type" value={rxCond.reversibility}
                                    onChange={setRxField('reversibility')}
                                    options={[{ value: 'reversible', label: 'Reversible' }, { value: 'irreversible', label: 'Irreversible' }]} />
                                </div>
                                <RxToggle disabled={isLocked} label="Rate Law — Exponents" value={rxCond.exponentMode}
                                  onChange={setRxField('exponentMode')}
                                  options={[{ value: 'fixed_1', label: 'Fixed = 1' }, { value: 'regress', label: 'Regress' }]} />
                              </>
                            )}
                            <div className="hx-eyebrow hx-eyebrow--spaced">
                              {ex.modelType === 'lhhw' ? 'LHHW' : 'Power Law'} —{' '}
                              {ex.reversibility === 'reversible' ? 'Reversible' : 'Irreversible'} (reference equation)
                            </div>
                            {eq && <div className="hx-eq">{eq}</div>}
                          </Panel>

                          {rxCond && (
                            <Panel title="Reaction Conditions" hint="(editable)">
                              <div className="hx-row">
                                <RxToggle disabled={isLocked} label="Reactor" value={rxCond.reactorMode}
                                  onChange={setRxField('reactorMode')}
                                  options={[{ value: 'batch', label: 'Batch' }, { value: 'pfr', label: 'PFR' }]} />
                                <RxToggle disabled={isLocked} label="Reaction Phase" value={rxCond.reactionPhase}
                                  onChange={setRxField('reactionPhase')}
                                  options={[{ value: 'liquid', label: 'Liquid' }, { value: 'vapor', label: 'Vapor' }]} />
                              </div>
                              <div className="hx-row">
                                <RxToggle disabled={isLocked} label="Concentration Basis" value={rxCond.concentrationBasis}
                                  onChange={setRxField('concentrationBasis')}
                                  options={[{ value: 'mole_fraction', label: 'Mole Fraction' }, { value: 'mass_fraction', label: 'Mass Fraction' }]} />
                                <RxToggle disabled={isLocked} label="Rate Basis" value={rxCond.rateBasis}
                                  onChange={setRxField('rateBasis')}
                                  options={[{ value: 'cat_weight', label: 'Catalyst weight' }, { value: 'volume', label: 'Reaction volume' }]} />
                              </div>
                              <div className="hx-row">
                                <RxField disabled={isLocked} label="Catalyst Weight" unit="g"
                                  value={rxCond.wCatG} onChange={setRxField('wCatG')} />
                                <RxField disabled={isLocked} label="Reactor Volume" unit="L" step={0.01}
                                  value={rxCond.vRxL} onChange={setRxField('vRxL')} />
                              </div>
                              {/* A PFR has no batch charge, and its temperature
                                  is a CSV column rather than one scalar — both
                                  hidden rather than shown as misleading blanks. */}
                              {rxCond.reactorMode === 'batch' && (
                                <div className="hx-row">
                                  <RxField disabled={isLocked} label="Initial Charge" unit="mol" step={0.01}
                                    value={rxCond.nMixMol} onChange={setRxField('nMixMol')} />
                                  <RxField disabled={isLocked} label="Temperature" unit="°C" step={1}
                                    value={rxCond.reactionTempC} onChange={setRxField('reactionTempC')} />
                                </div>
                              )}
                              <div className="hx-row">
                                <RxField disabled={isLocked} label={isMayman ? 'Reaction Pressure (fallback, psia)' : 'Reaction Pressure'} step={0.01}
                                  value={rxCond.reactionPressure} onChange={setRxField('reactionPressure')} />
                                <div className="hx-field">
                                  <label className="hx-field-label" htmlFor="hx-punit">Unit</label>
                                  <select id="hx-punit" className="hx-select" value={rxCond.pressureUnit}
                                    disabled={isLocked}
                                    onChange={e => setRxField('pressureUnit')(e.target.value)}>
                                    {['atm', 'bar', 'kPa', 'psi'].map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                </div>
                              </div>
                              {rxCond.reactorMode === 'pfr' && (
                                <p className="hx-body hx-body--dim">
                                  Temperature, pressure and residence time come from the T, Pressure and τ
                                  columns of the data set, one value per run.
                                </p>
                              )}
                            </Panel>
                          )}
                        </div>

                        {/* Right column — the panel that used to force a long
                            scroll on LHHW cases. */}
                        <Panel title="Initial Estimates"
                          hint={diverged ? '(updated for your edited settings)' : '(starting guesses — reference setup)'}>
                          {diverged && (
                            <p className="hx-callout">
                              These no longer match the reference example — they follow the model,
                              reaction type and exponent settings you changed on the left.
                            </p>
                          )}
                          <div className="hx-tablewrap hx-tablewrap--tall">
                            <table className="hx-table">
                              <thead>
                                <tr>
                                  {['Parameter', 'Lower', 'Value', 'Upper'].map(h => (
                                    <th key={h} scope="col">{h}</th>
                                  ))}
                                  <th scope="col" className="hx-th-center">Fixed</th>
                                </tr>
                              </thead>
                              <tbody>
                                {estimates.map((row, i) => (
                                  <tr key={i}>
                                    <td className="hx-td-name">{row.name}</td>
                                    <td>{row.lower}</td>
                                    <td>{typeof row.value === 'number' && row.value >= 1e5
                                      ? row.value.toExponential(2) : row.value}</td>
                                    <td>{typeof row.upper === 'number' && row.upper >= 1e5
                                      ? row.upper.toExponential(0) : row.upper}</td>
                                    <td className="hx-td-center">
                                      <input type="checkbox" checked={false} disabled readOnly />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Panel>
                      </div>

                      <div className="hx-actions">
                        <button type="button" className="hx-btn hx-btn--ghost"
                          onClick={() => setWizardStep('data')}>← Back</button>
                        <button type="button" className="hx-btn hx-btn--primary hx-btn--run"
                          disabled={runStatus === 'running'}
                          onClick={() => { runRegression(ex); setWizardStep('graph'); }}>
                          {runStatus === 'running' ? ('⏳ ' + runningLabel) : '▶ Run Fitting'}
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── STEP 3 · GRAPH ── */}
                  {wizardStep === 'graph' && (
                    <>
                      <div className="hx-2col">
                        <Panel title={
                          runStatus === 'running' ? 'Chart — Live Convergence (updating…)'
                            : runStatus === 'done' ? graphTitle
                              : runStatus === 'timeout' ? 'Chart — Best-so-far result (optimizer timed out)'
                                : runStatus === 'error' ? 'Chart — Run Failed' : 'Chart'}>
                          {runStatus === 'running' && (
                            (progress?.iter_log && progress.iter_log.length > 1) ? (
                              <MiniChart xLabel="Iteration" yLabel="Loss (lower = better)"
                                seriesList={[{
                                  name: 'Loss',
                                  color: sysMeta.color,
                                  points: progress.iter_log.map(e => ({ x: e.iter, y: e.loss })).filter(p => p.y != null),
                                }]} />
                            ) : (
                              <p className="hx-empty">Waiting for the first iteration…</p>
                            )
                          )}

                          {hasFitResponse && (
                            <>
                              <DatasetTabs />
                              {graphPlot ? (
                                <img className="hx-plot"
                                  alt={isMayman
                                    ? 'Backend parity plot showing observed versus predicted outlet mole fractions by species'
                                    : 'Backend composition profile showing experimental measurements and fitted model'}
                                  src={`data:image/png;base64,${graphPlot}`} />
                              ) : (
                                <p className="hx-empty">
                                  The backend completed this fit but did not return a plot. No data-only substitute is shown.
                                </p>
                              )}
                              {isMayman && graphPlot && (
                                <div className="hx-review-banner" role="note">
                                  This is the backend observed-versus-predicted parity plot, not the paper’s own kinetic-model validation. The source Table 1 reports conversion/selectivity; the supplied workbook converts those values to outlet mole fractions. CO and CO₂ are combined as COx. O₂ and H₂O outlets are derived and receive zero objective weight. The platform uses a simplified three-reaction network and a documented equivalent-τ conversion; do not interpret this as reproduction of the paper’s full mechanistic model.
                                </div>
                              )}
                              {isMulti && !activeRunPlot && mainPlot && (
                                <p className="hx-body hx-body--dim">
                                  The backend did not return a plot for this individual dataset; the image above is the combined multi-dataset overview.
                                </p>
                              )}
                            </>
                          )}

                          {runStatus === 'error' && <p className="hx-error">{runError}</p>}
                        </Panel>

                        <div className="hx-stack">
                          <Panel title="Run Status">
                            <div className="hx-mono">
                              {runStatus === 'running' && (
                                <>
                                  {runningLabel}
                                  <div className="hx-progress">
                                    <div className="hx-progress-bar" style={{
                                      width: progress?.n_iter
                                        ? `${Math.min(100, (progress.iter_done / progress.n_iter) * 100)}%`
                                        : '10%',
                                    }} />
                                  </div>
                                </>
                              )}
                              {runStatus === 'done' && (
                                <span className="hx-ok">✅ Response received in {(elapsedMs / 1000).toFixed(1)}s.</span>
                              )}
                              {runStatus === 'timeout' && (
                                <span className="hx-error">⚠ Optimizer timed out. The plots and parameters are best-so-far only ({(elapsedMs / 1000).toFixed(1)}s).</span>
                              )}
                              {runStatus === 'error' && <span className="hx-error">❌ Run failed.</span>}
                            </div>
                          </Panel>

                          {(runStatus === 'running' || runStatus === 'done' || runStatus === 'timeout') && progress?.iter_log?.length > 0 && (
                            <Panel title={`Live Iterations ${runStatus === 'running' ? '(updating…)' : '(final)'}`}>
                              <div className="hx-log">
                                {progress.iter_log.slice().reverse().map((entry, i) => (
                                  <div key={i}>
                                    Iteration #{entry.iter}, Function Value ={' '}
                                    {entry.loss != null ? entry.loss.toExponential(6) : '—'}
                                    {entry.params && `, Parameters = {${Object.entries(entry.params)
                                      .map(([k, v]) => `${k}=${typeof v === 'number' ? v.toFixed(4) : v}`).join(', ')}}`}
                                  </div>
                                ))}
                                {runStatus === 'done' && <div>Response received.</div>}
                                {runStatus === 'timeout' && <div className="hx-error">Timed out; best-so-far result only.</div>}
                              </div>
                            </Panel>
                          )}
                        </div>
                      </div>

                      <div className="hx-actions hx-actions--end">
                        {hasFitResponse && (
                          <button type="button" className="hx-btn hx-btn--primary"
                            onClick={() => setWizardStep('params')}>View Parameters →</button>
                        )}
                        {runStatus === 'error' && (
                          <button type="button" className="hx-btn hx-btn--ghost"
                            onClick={() => setWizardStep('model')}>← Back to Model Input</button>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── STEP 4 · PARAMETERS ── */}
                  {wizardStep === 'params' && (
                    <>
                      <div className="hx-3col">
                        <Panel title="Regression Results">
                          <div className="hx-mono">
                            {runStatus === 'idle' && 'Not run yet.'}
                            {runStatus === 'error' && <span className="hx-error">❌ {runError}</span>}
                            {hasFitResponse && (
                              <>
                                {runStatus === 'timeout' ? <span className="hx-error">⚠ Timed out — best-so-far result only.</span> : <span className="hx-ok">✅ Response received.</span>} {(elapsedMs / 1000).toFixed(1)}s elapsed.
                                {displayR2Entries.map(([sp, v]) => (
                                  <div key={sp}>R²({sp}) = {typeof v === 'number' ? v.toFixed(4) : String(v)}</div>
                                ))}
                              </>
                            )}
                          </div>
                        </Panel>

                        <Panel title="Latest Parameters">
                          <div className="hx-mono">
                            {!hasFitResponse && 'Run the regression to see fitted values.'}
                            {hasFitResponse && (
                              isMultiReactionExample && reactionParams.length
                                ? reactionParams.map((rp, i) => (
                                    <div key={i} className="hx-param-rxn" style={{ marginBottom: i === reactionParams.length - 1 ? 0 : 10 }}>
                                      <strong>Reaction {rp.reaction_index ?? i + 1}</strong><br />
                                      Af: {rp.Af != null ? Number(rp.Af).toExponential(3) : '—'}<br />
                                      Ea_f: {rp.Ea_kJ_mol != null ? Number(rp.Ea_kJ_mol).toFixed(2) : '—'} kJ/mol<br />
                                      {rxCond?.reversibility === 'reversible' && (
                                        <>
                                          Ab: {rp.Ab != null ? Number(rp.Ab).toExponential(3) : '—'}<br />
                                          Ea_b: {rp.Ea_rev_kJ_mol != null ? Number(rp.Ea_rev_kJ_mol).toFixed(2) : '—'} kJ/mol<br />
                                        </>
                                      )}
                                      {rp.model_type === 'lhhw' && <><span>Model: LHHW</span><br /></>}
                                    </div>
                                  ))
                                : <>
                                    Af: {paramAf != null ? paramAf.toExponential(3) : '—'}<br />
                                    Ea_f: {paramEaF != null ? paramEaF.toFixed(2) : '—'} kJ/mol<br />
                                    {rxCond?.reversibility === 'reversible' && (
                                      <>
                                        Ab: {paramAb != null ? paramAb.toExponential(3) : '—'}<br />
                                        Ea_b: {paramEaB != null ? paramEaB.toFixed(2) : '—'} kJ/mol<br />
                                      </>
                                    )}
                                  </>
                            )}
                          </div>
                        </Panel>

                        <Panel title="Setup Used">
                          <div className="hx-mono">
                            <div>Reactor: {rxCond?.reactorMode?.toUpperCase()} · {rxCond?.reactionPhase}</div>
                            <div>Model: {rxCond?.modelType === 'lhhw' ? 'LHHW' : 'Power Law'}</div>
                            <div>Reaction: {rxCond?.reversibility}</div>
                            <div>Exponents: {rxCond?.exponentMode === 'fixed_1' ? 'Fixed = 1' : 'Regressed'}</div>
                            <div>Data sets: {datasets.length} ({datasets.reduce((n, d) => n + d.rows.length, 0)} points)</div>
                            {isMayman && <><div>Pressure group: {datasets[0]?.meta?.pressureLabel} · workbook points {datasets[0]?.meta?.pointRange}.</div><div>Objective weights: O₂ = 0, H₂O = 0 (derived from conversion / selectivity balances); CO + CO₂ are combined as COx.</div><div>Stage 1: one pressure-specific dataset only. The combined three-pressure fit is deferred until these runs are reviewed.</div></>}
                          </div>
                        </Panel>
                      </div>

                      {runStatus === 'done' && songEaBenchmark && (
                        <Panel title={`Literature activation-energy check — ${ex.system === 'meoac' ? 'Methyl Acetate' : 'DME'} LHHW`} className="hx-stack">
                          <div className="hx-mono">
                            <div>Paper apparent forward Ea ({songEaBenchmark.equation}): {songEaBenchmark.kcal.toFixed(2)} kcal/mol ({songEaBenchmark.kJ.toFixed(2)} kJ/mol)</div>
                            <div>Fitted forward Ea from backend: {fittedEaValid ? `${fittedEa.toFixed(2)} kJ/mol` : 'Not returned in this response'}</div>
                            {fittedEaValid && (
                              <>
                                <div>Fitted − paper benchmark: {eaDelta >= 0 ? '+' : ''}{eaDelta.toFixed(2)} kJ/mol</div>
                                <div>Relative difference: {eaPercent >= 0 ? '+' : ''}{eaPercent.toFixed(1)}%</div>
                              </>
                            )}
                          </div>
                          <div className="hx-review-banner" role="note">
                            Compare only a multi-temperature LHHW fit with the corresponding Song equation. This is a parameter consistency check, not independent validation: the example multi-run trajectories are generated from the paper model where supporting-information measurements are unavailable. The DME source paper also has a temperature inconsistency between Table 7 and its caption, so interpret that comparison cautiously. A poor R² or mismatched plot means the fitted Ea should not be claimed as recovered from literature.
                          </div>
                        </Panel>
                      )}

                      <div className="hx-actions">
                        <button type="button" className="hx-btn hx-btn--ghost" onClick={backToList}>
                          🔄 Try another example
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpExamples;